import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listContentItems,
  listLayouts,
  listPages,
  saveManualPage,
  saveManualKindPage,
  clearPage,
  getPrintQualityCheck,
  getBookSnapshot,
  restoreBookSnapshot,
  discardBookSnapshot,
  updatePageContent,
  fetchInteriorPagePreviewHtml,
  fetchCoverPreviewHtml,
  composeBook,
  uploadPhoto,
  addTextItem,
  deleteContentItem,
  deleteAllContentItems,
  estimatePrice,
  getRecommendedPageCount,
  extendBookPages,
  shrinkBookPages,
  movePage,
  updateContentItem,
  duplicateBook,
  fetchBookRow,
  updateBookRow
} from '../../../services/compositionApi';
import { fetchCollective } from '../../../services/compositionApi';
import { listPrintFormats } from '../../../services/ordersApi';
import { formatPriceCents as formatEuros, formatPriceCentsDelta as formatEurosDelta } from '../../../utils/orderWorkflow';
import AtelierSidebar from './AtelierSidebar';
import AtelierFlipBookView from './AtelierFlipBookView';
import AtelierLayoutPanel from './AtelierLayoutPanel';
import AtelierPageOverlay from './AtelierPageOverlay';
import AtelierCoverPanel from './AtelierCoverPanel';
import { coverPatchFor } from './store/coverPatch';
import AtelierGenerateModal from './AtelierGenerateModal';
import AtelierConfirmSwitchDialog from './AtelierConfirmSwitchDialog';
import AtelierOnboarding from './AtelierOnboarding';
import AtelierFinishModal from './AtelierFinishModal';
import AtelierDuplicatePhotosModal from './AtelierDuplicatePhotosModal';
import CollectiveInvitePanel from '../collective/CollectiveInvitePanel';
import CollectiveManagePanel from '../collective/CollectiveManagePanel';
import AtelierPageFilmstrip from './AtelierPageFilmstrip';
import AtelierPhotoPanel, { AtelierTextPanel } from './AtelierPhotoPanel';
import AtelierPhotoPickPanel from './AtelierPhotoPickPanel';
import BookipixLoader from './BookipixLoader';
import AtelierPageKindOverlay from './AtelierPageKindOverlay';
import { slotCroppedFraction } from './slotCrop';
import { applyLiveToPhoto } from './framingLive';
import AtelierPageActions from './AtelierPageActions';
import AtelierToolsBar from './AtelierToolsBar';
import { findAtelierLayout, ATELIER_PAGE_KIND_VALUES, slotAcceptsItem } from './atelierLayouts';
import { getOverlayGeometry, getOverlayInsetPct } from './atelierLayoutGeometry';
import { FORMAT_DIMENSIONS_MM } from './photoQuality';
import { BUILTIN_FORMAT_IDS, isCatalogFormat, catalogFormatName, formatCm, pageLimits } from '../../../utils/printFormats';
import { spreadPair, spreadCount, spreadOfPage, facingPageIndex, isLeftPage } from '../../../utils/pageParity';
import AnonymousBanner from '../../common/AnonymousBanner';
import AtelierToasts from './AtelierToasts';
import {
  newSlotIds, normalizeFraming, isDefaultFraming, framingFromAdjustment, adjustmentFromFraming
} from './framing';
import {
  BookStoreProvider, useBookState, useBookActions, useUiField, useSaveEngine
} from './store/BookStoreProvider';
import { BOOK_ACTIONS, getPageModel } from './store/bookReducer';
import {
  modelFromRow, createEmptyModel, describePage, itemIdsOfModel, DEFAULT_EMPTY_PAGE_LAYOUT
} from './store/pageModel';
import { createPreviewLoader, pageKey, COVER_KEYS } from './store/previewLoader';
import '../../../styles/luxe-theme.css';
import './BookAtelierLuxe.css';
// (Les classes .modal-* importees ci-dessous servent aux fenetres de l atelier.)
// definies dans DashboardLuxe.css (.modal-card, .modal-form-field,
// .modal-checkbox-field...) — absentes de BookAtelierLuxe.css et
// incompatibles avec celles de BookLuxe.css (qui utilise .modal-content, pas
// .modal-card). Sans cet import, la modale d'activation s'afficherait sans
// aucun style depuis l'atelier.
import '../../dashboard/DashboardLuxe.css';

// Atelier de creation personnalisee : l'utilisateur construit son livre
// page par page, en choisissant une mise en page prealable puis en y
// glissant ses photos/souvenirs — jamais de positionnement libre (cahier
// des charges §4). Trois zones : Mes souvenirs (gauche, AtelierSidebar) /
// Le livre (centre, feuilletage reel — AtelierFlipBookView, voir son
// en-tete ; AtelierBookView.js reste a cote, non importe, comme filet de
// secours) / Mise en page (droite, AtelierLayoutPanel).
//
// Sauvegarde : automatique des que tous les emplacements du format choisi
// sont remplis (jamais de bouton "Valider" separe) — meme philosophie
// "instantane" que Configuration/l'editeur de couverture. La page est
// toujours sauvegardee verrouillee (locked=true, voir
// backend/routes/composition.js) : une recomposition automatique ulterieure
// ne l'ecrasera jamais.
// Vu une fois -> jamais reaffiche automatiquement (bouton "?" du header
// pour le rouvrir volontairement) — memorise par livre, pas globalement :
// un nouveau livre reste un premier contact avec l'atelier.
const ONBOARDING_SEEN_KEY_PREFIX = 'atelierOnboardingSeen_';

// Valeurs vides PARTAGEES : un tableau/objet neuf a chaque rendu casserait les
// comparaisons par identite (useMemo, memo) des composants enfants.
const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_KIND_FIELDS = Object.freeze({ title: '', number: '', text: '', photoItemId: null });

// Quelle page "sans mise en page" a une page ? Utilise pour les statistiques
// (pastilles de la pellicule, ecran "Terminer mon livre") — l'equivalent de
// l'ancienne "ligne de page" serveur, deduit du modele editable.
function summarizePage(model, layoutsById) {
  if (!model) return { hasRow: false, slug: null, itemIds: EMPTY_ARRAY, kind: null };
  const description = describePage(model);
  if (description.k === 'kind') {
    return {
      hasRow: true, slug: null, itemIds: EMPTY_ARRAY, kind: description.kind,
      text: description.fields.text, title: description.fields.title, photoItemId: description.fields.photoItemId
    };
  }
  if (description.k === 'layout') {
    return { hasRow: true, slug: description.slug, itemIds: description.itemIds, kind: null };
  }
  if (description.k === 'foreign') {
    const row = model.foreign;
    const content = row.content || {};
    const layout = row.layout_id ? layoutsById[row.layout_id] : null;
    return {
      hasRow: true,
      slug: layout ? layout.slug : null,
      itemIds: Array.isArray(content.itemIds) ? content.itemIds : EMPTY_ARRAY,
      kind: content.kind || null,
      text: content.text,
      title: content.title,
      photoItemId: content.photoItemId
    };
  }
  return { hasRow: false, slug: null, itemIds: EMPTY_ARRAY, kind: null };
}

// Une page porte-t-elle du travail MANUEL ? (l'ancien "locked" cote serveur :
// toute page ecrite depuis l'atelier est verrouillee).
const isManualPage = (model) => {
  if (!model) return false;
  const kind = describePage(model).k;
  return kind === 'layout' || kind === 'kind';
};

function BookAtelierInner() {
  const { t, i18n } = useTranslation('atelier');
  const { bookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Jamais l'erreur brute du serveur a l'ecran : un message simple. Une limite
  // de debit (429, "Trop de requetes...") se dit comme une connexion perdue.
  const friendlyMessage = (err, fallback) => (err && err.status === 429 ? t('store.states.networkError') : fallback);

  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  // Page sur laquelle se placer une fois le livre reconstruit apres un ajout de pages.
  const [pendingPageTarget, setPendingPageTarget] = useState(null);
  const lockedToldRef = useRef(false);
  // « Continuer sur la page suivante » (correctif 7) : { pageIndex, text } — voir l'effet plus bas.
  const [continuation, setContinuation] = useState(null);

  const [bookRow, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Mode « Agrandir » (touche F) : le panneau se range et la page prend la place ; sous 1000 px le
  // panneau est un tiroir ouvert a la demande (bouton « Outils »).
  const [focusMode, setFocusMode] = useState(false);
  const [panelDrawerOpen, setPanelDrawerOpen] = useState(false);
  // En entrant en mode Agrandir on remonte la page en haut de l'ecran (sinon elle s'agrandit sous la
  // barre du haut et on ne voit pas le gain) ; en sortant on revient en haut.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (focusMode) {
        const stage = document.querySelector('.atelier-stage-column');
        if (stage) window.scrollTo({ top: Math.max(0, stage.getBoundingClientRect().top + window.scrollY - 8), behavior: 'smooth' });
      } else if (window.scrollY > 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [focusMode]);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem(`${ONBOARDING_SEEN_KEY_PREFIX}${bookId}`)
  );
  // "Inviter des proches" (AtelierPartagerLien) : ouvre l'activation si le
  // mode collectif n'est pas encore active, sinon navigue directement vers
  // sa gestion (retour utilisateur, 2026-09-28).
  // Panneau du collectif pose sur l'atelier (refonte du collectif) : null, 'invite' (activation) ou 'manage' (« Mon collectif »).
  const [panneauCollectif, setPanneauCollectif] = useState(null);
  // Jauge du bouton Inviter : personnes ayant ajoute des souvenirs / invites (phase 2).
  const [jaugeCollectif, setJaugeCollectif] = useState(null);
  // Bandeau "Mode test" DETACHABLE (Gabarit 2, retour utilisateur 2026-09-29) :
  // refermable "pour de bon pendant la session" — un simple etat React suffit,
  // pas besoin de le retenir au-dela (pas de localStorage).
  const [showTestBanner, setShowTestBanner] = useState(true);
  // "Dupliquer pour modifier" (artefact "Atelier Bookipix", 2026-10-11) —
  // voir handleDuplicateBook plus bas et POST /api/books/:bookId/duplicate.
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');

  const [items, setItems] = useState([]);
  const [layouts, setLayouts] = useState([]);

  // Prix discret de l'atelier (chantier "tarification dynamique",
  // 2026-09-27, §6) — { bookPriceCents } le plus recent connu pour ce
  // format/cette pagination. priceDelta : indication BREVE ("+2 pages ·
  // +2,20 €") affichee au moment d'un ajout/retrait de pages ou d'un
  // changement de format, puis effacee toute seule (§7/§8/§11) — jamais un
  // panneau permanent, jamais un second calcul de la formule ici (voir
  // estimatePrice -> GET /orders/book/:id/price-estimate, deja importe).
  const [priceInfo, setPriceInfo] = useState(null);
  const [priceDelta, setPriceDelta] = useState(null);
  const priceDeltaTimeoutRef = useRef(null);

  // Navigation et selection : etat d'INTERFACE (store ui), jamais enregistre.
  const [viewIndex, setViewIndex] = useUiField('viewIndex'); // 0 = couverture, dernier = 4e, sinon double-page
  const [selectedSide, setSelectedSide] = useUiField('selectedSide');
  const [selectedSidebarItem, setSelectedSidebarItem] = useUiField('selectedSidebarItem');

  // Le contenu de la page courante (mise en page, emplacements, cadrages,
  // legendes, styles de texte, pages "sans emplacement") vit dans le STORE du
  // livre (modele de page, voir store/pageModel.js) : plus de brouillons
  // paralleles ici. Les valeurs "draft*" utilisees plus bas en sont deduites.
  //
  // Fenetres et selecteurs ouverts : etat d'INTERFACE, jamais enregistre.
  // Photo SELECTIONNEE pour le cadrage (barre flottante, phase 4a) : { type:
  // 'slot', pageIndex, slotIndex } | { type: 'cover', face } | null. Plus aucune
  // fenetre de cadrage.
  const [selectedFraming, setSelectedFraming] = useUiField('selectedFraming');
  // Itemid dont l'ecran recapitulatif ("Terminer mon livre") demande la
  // selection directe (voir l'effet plus bas).
  const [pendingAdjustItemId, setPendingAdjustItemId] = useUiField('pendingAdjustItemId');
  // Emplacement de TEXTE en cours d'edition sur la page (le panneau de gauche s'y adapte).
  const [editingTextSlot, setEditingTextSlot] = useUiField('editingTextSlot');
  // Selecteur de photos sur un emplacement vide (index) ou une face de
  // couverture ('front' | 'back'), et l'etat de l'import direct qui peut s'y faire.
  const [pickerTargetSlotIndex, setPickerTargetSlotIndex] = useUiField('pickerTargetSlotIndex');
  const [pickerTargetCoverFace, setPickerTargetCoverFace] = useUiField('pickerTargetCoverFace');
  const [pickerUploading, setPickerUploading] = useState(false);
  const [pickerUploadError, setPickerUploadError] = useState('');

  // QUEL ONGLET EST ACTIF dans le panneau fixe de gauche (refonte 2026-10-11,
  // artefact "Atelier Bookipix" : "un panneau qui ne bouge plus... le
  // panneau reste a gauche, la page reste au centre, rien ne saute").
  //
  // Annule le choix du 2026-09-25 ("le livre est au centre, les outils sont
  // secondaires") qui avait sorti Photos/Mise en page d'une grille fixe pour
  // en faire des TIROIRS (AtelierDrawer.js) ouverts a la demande par-dessus
  // ou a cote du livre — exactement le defaut que la maquette signale :
  // le panneau apparaissait/disparaissait et le livre se decalait. Le
  // panneau est desormais TOUJOURS visible, a la meme place ; seul SON
  // CONTENU change entre les deux onglets. 'photos' | 'layout', jamais
  // null : il n'y a plus de notion de tiroir "ferme" (meme logique que la
  // pellicule de pages, rendue toujours visible plus tot le meme jour).
  // Defaut 'photos' (retour utilisateur, 2026-10-06 : "je prefere que
  // l'onglet Photo reste toujours affiche par defaut").
  const [activeDrawer, setActiveDrawer] = useUiField('activeDrawer');

  // Un changement d'onglet du panneau (Photos / Mise en page) referme la selection :
  // le panneau "photo selectionnee" y prend sa place, il doit rendre la main.
  useEffect(() => { setSelectedFraming(null); }, [activeDrawer]); // eslint-disable-line react-hooks/exhaustive-deps


  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generateVariant, setGenerateVariant] = useState(0);
  const [estimatedPages, setEstimatedPages] = useState(null);
  // LE LIVRE VA-T-IL GRANDIR, ET DE COMBIEN COUTERA-T-IL (2026-09-21) ?
  //
  // Le moteur place TOUT le contenu sans jamais le tronquer, puis le livre
  // est redimensionne pour le contenir : 69 photos ne tiennent pas en 30
  // pages. C'est defendable — mais ca se faisait en silence, alors que le
  // PRIX suit le nombre de pages. On choisissait 30, on se retrouvait avec
  // 46, et on l'apprenait a la commande.
  // { pagesPrevues, prixActuelCents, prixPrevuCents } ou null.
  const [debordement, setDebordement] = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [isConfirmSwitchOpen, setIsConfirmSwitchOpen] = useState(false);

  // Resynchronise la BIBLIOTHEQUE apres une ecriture de page.
  //
  // Le serveur supprime les souvenirs ecrits dans une page puis abandonnes
  // (voir bookContentService.purgeAbandonedPageTexts) : sans ce
  // rafraichissement, la vignette d'un souvenir deja supprime resterait
  // affichee dans l'onglet "Souvenirs", cliquable, et poserait un id mort.
  //
  // Appele UNIQUEMENT apres qu'une ecriture de page a repondu : c'est le
  // serveur qui decide quels souvenirs disparaissent, relire avant son
  // verdict renverrait l'etat d'AVANT. Et jamais a chaque frappe : seulement
  // quand un texte a reellement quitte la page (voir les appelants).
  const resyncItems = useCallback(async () => {
    if (!bookId) return;
    try {
      const frais = await listContentItems(bookId);
      setItems(frais || []);
    } catch (_err) {
      // Non bloquant : au pire la bibliotheque se resynchronise au prochain
      // chargement de l'atelier.
    }
  }, [bookId]);

  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  // Selection en attente de decision quand elle contient des doublons :
  // { doublons: File[], selection: File[] }. Rien nest envoye tant que
  // lutilisateur na pas tranche (voir AtelierDuplicatePhotosModal).
  const [doublonsEnAttente, setDoublonsEnAttente] = useState(null);
  // { done, total, failed } pendant un envoi de lot, null sinon.
  const [uploadProgress, setUploadProgress] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [sidebarAddError, setSidebarAddError] = useState('');
  const [addingPages, setAddingPages] = useState(false);
  const [removingPages, setRemovingPages] = useState(false);
  const [movingPage, setMovingPage] = useState(false);

  // Force un rechargement de l'apercu de la vue courante meme quand
  // viewIndex ne change pas (ex. sauvegarde de couverture, generation
  // automatique alors qu'on est deja sur la premiere double-page) : l'effet
  // de chargement ci-dessous ne se redeclenche que sur un changement de
  // dependance, jamais sur un simple `setCoverHtml(null)` isole.
  // Photos signalees par le controle qualite d'impression, regroupees PAR
  // PAGE : { [pageIndex]: nombre }.
  //
  // Jusqu'ici ce controle n'etait consulte qu'a l'ouverture de "Terminer mon
  // livre", et son unique lien sautait a la PREMIERE page concernee. Quand
  // celle-ci etait la page ou l'on se trouvait deja (cas frequent : page 1),
  // cliquer ne produisait rien de visible — "ca ne renvoie nulle part"
  // (2026-09-14). La reponse est de MONTRER ou sont les problemes plutot que
  // d'y teleporter : une pastille sur chaque vignette concernee.
  //
  // Jamais bloquant : un echec laisse simplement la carte vide, l'atelier
  // fonctionne exactement comme avant.
  const [qualityWarnings, setQualityWarnings] = useState([]);
  // Photos entre 100 et 150 dpi : point d'attention (jamais bloquant) dans la barre des pages.
  const [qualityLimits, setQualityLimits] = useState([]);

  // Point de restauration disponible ({createdAt, pageCount, manualPages, ...}
  // ou null). Pose automatiquement avant une generation automatique — c'est ce
  // qui rend le bouton essayable : « j'aimerais le tester mais sans detruire
  // ce que je viens de faire manuellement » (2026-09-15).
  //
  // Null aussi quand la migration phase19 n'a pas encore ete jouee : dans ce
  // cas aucun retour en arriere n'est propose, plutot qu'une promesse qui
  // echouerait au moment ou l'utilisateur compte dessus.
  const [snapshot, setSnapshot] = useState(null);
  const [restoringSnapshot, setRestoringSnapshot] = useState(false);

  const refreshSnapshot = useCallback(async () => {
    if (!bookId) return;
    try {
      const { snapshot: point } = await getBookSnapshot(bookId);
      setSnapshot(point || null);
    } catch (_err) {
      setSnapshot(null);
    }
  }, [bookId]);

  const refreshQualityWarnings = useCallback(async () => {
    if (!bookId) return;
    try {
      const result = await getPrintQualityCheck(bookId);
      setQualityWarnings(Array.isArray(result?.warnings) ? result.warnings : []);
      setQualityLimits(Array.isArray(result?.limites) ? result.limites : []);
    } catch (_err) {
      setQualityWarnings([]);
      setQualityLimits([]);
    }
  }, [bookId]);

  // --- ETAT DU LIVRE (store) -------------------------------------------------
  // Pages, couverture, historique annuler/retablir et statut d'enregistrement
  // vivent dans UN store (store/bookReducer.js). Ce composant ne tient plus
  // que ce qui n'est PAS du contenu du livre : la ligne serveur (format,
  // nombre de pages...), la bibliotheque de photos, l'interface.
  const bookState = useBookState();
  const { dispatch: bookDispatch, pushToast, dismissToast } = useBookActions();

  // Trois etats seulement montres a l'utilisateur : saving / saved / offline.
  const saveStatus = bookState.status;

  // `book` = la ligne serveur + le titre et les reglages de couverture TELS
  // QUE LE STORE LES VOIT : une seule verite pour ce que l'utilisateur est en
  // train de regler (le panneau de couverture n'en garde plus de copie).
  const book = useMemo(() => {
    if (!bookRow) return bookRow;
    const cover = bookState.cover;
    return cover ? { ...bookRow, title: cover.title, cover_overrides: cover.overrides } : bookRow;
  }, [bookRow, bookState.cover]);

  // --- APERCUS : seulement ce qui est a l'ecran, 4 requetes au plus en meme
  // temps, avec cache (store/previewLoader.js). Avant : les 30 pages d'un coup.
  const previewLoaderRef = useRef(null);
  if (!previewLoaderRef.current) {
    previewLoaderRef.current = createPreviewLoader({
      fetchPage: (index) => fetchInteriorPagePreviewHtml(bookId, index),
      fetchCover: (face) => fetchCoverPreviewHtml(bookId, face, { placeholders: true, lang: i18n.language })
    });
  }
  const previewLoader = previewLoaderRef.current;
  const previewVersion = useSyncExternalStore(previewLoader.subscribe, previewLoader.getVersion);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pagePreviewCache = useMemo(() => previewLoader.snapshotPages(), [previewLoader, previewVersion]);
  const coverHtml = previewLoader.get(COVER_KEYS.front) ?? null;
  const backCoverHtml = previewLoader.get(COVER_KEYS.back) ?? null;
  const loadingPreview = previewLoader.isLoading();
  const previewFailed = previewLoader.hasFailures();

  const refreshPagePreview = useCallback(async (pageIndex) => {
    if (pageIndex == null) return;
    previewLoader.invalidate([pageKey(pageIndex)]);
  }, [previewLoader]);

  // Le controle qualite d'impression coute un calcul serveur : un seul appel,
  // un peu apres la derniere sauvegarde, jamais un par modification.
  const qualityTimerRef = useRef(null);
  const scheduleQualityRefresh = useCallback(() => {
    if (qualityTimerRef.current) clearTimeout(qualityTimerRef.current);
    qualityTimerRef.current = setTimeout(() => { qualityTimerRef.current = null; refreshQualityWarnings(); }, 2500);
  }, [refreshQualityWarnings]);
  useEffect(() => () => { if (qualityTimerRef.current) clearTimeout(qualityTimerRef.current); }, []);

  // --- MOTEUR DE SAUVEGARDE : le seul chemin d'ecriture, pour les pages comme
  // pour la couverture (store/saveEngine.js).
  const layoutsBySlug = useMemo(
    () => Object.fromEntries(layouts.map((layout) => [layout.slug, { id: layout.id, kind: layout.kind }])),
    [layouts]
  );
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // Souvenirs ecrits dans chaque page apres sa derniere sauvegarde : si l'un
  // d'eux quitte la page, le serveur peut le supprimer (voir resyncItems).
  const textIdsByPageRef = useRef({});
  const textIdsOfPlan = (plan) => {
    const ids = plan.payload?.itemIds || plan.payload?.content?.itemIds || [];
    return new Set(ids.filter((id) => itemsRef.current.find((item) => item.id === id && item.kind === 'texte')));
  };

  const io = {
    writePage: (index, plan) => {
      switch (plan.op) {
        case 'clear': return clearPage(bookId, index);
        case 'restore': return updatePageContent(bookId, index, { layoutId: plan.row.layout_id, content: plan.row.content, locked: Boolean(plan.row.locked) });
        case 'kind': return saveManualKindPage(bookId, index, plan.payload);
        case 'complete': return saveManualPage(bookId, index, plan.payload);
        case 'partial': return updatePageContent(bookId, index, plan.payload);
        default: return Promise.resolve();
      }
    },
    writeCover: async (cover) => {
      const { error: updateError } = await updateBookRow(bookId, { title: cover.title, cover_overrides: cover.overrides });
      if (updateError) throw updateError;
    },
    layoutsBySlug: () => layoutsBySlug,
    afterPageSaved: (index, plan) => {
      previewLoader.invalidate([pageKey(index)]);
      scheduleQualityRefresh();
      const now = textIdsOfPlan(plan);
      const before = textIdsByPageRef.current[index] || new Set();
      textIdsByPageRef.current[index] = now;
      if ([...before].some((id) => !now.has(id))) resyncItems();
    },
    // Couverture, dos et 4e ne forment qu'UNE feuille : on invalide les deux faces.
    afterCoverSaved: () => previewLoader.invalidate([COVER_KEYS.front, COVER_KEYS.back])
  };
  const engine = useSaveEngine(io);

  // Construit le modele de chaque page a partir des lignes serveur et le
  // charge dans le store. `extra` : { cover } au premier chargement,
  // { step } pour un pas d'historique unique (composition automatique).
  const loadModels = useCallback((rows, layoutList, itemList, pageCount, extra = {}) => {
    const layoutsMap = Object.fromEntries((layoutList || []).map((layout) => [layout.id, layout]));
    const itemsMap = Object.fromEntries((itemList || []).map((item) => [item.id, item]));
    const defaultSlug = (layoutList || []).some((layout) => layout.slug === DEFAULT_EMPTY_PAGE_LAYOUT) ? DEFAULT_EMPTY_PAGE_LAYOUT : null;
    const byIndex = new Map((rows || []).map((row) => [row.page_index, row]));
    const pages = {};
    const textIds = {};
    for (let index = 0; index < pageCount; index += 1) {
      const model = modelFromRow(byIndex.get(index), { layoutsById: layoutsMap, itemsById: itemsMap, defaultSlug });
      pages[index] = model;
      textIds[index] = new Set([...itemIdsOfModel(model)].filter((id) => itemsMap[id]?.kind === 'texte'));
    }
    textIdsByPageRef.current = textIds;
    bookDispatch({ type: BOOK_ACTIONS.LOAD, pageCount, defaultSlug, pages, ...extra });
  }, [bookDispatch]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // On ecrit d'abord ce qui est en attente : on relit ensuite la verite.
      await engine.flush();
      const [{ data: loadedBook, error: bookError }, itemList, layoutList, pageList] = await Promise.all([
        fetchBookRow(bookId),
        listContentItems(bookId),
        listLayouts(),
        listPages(bookId)
      ]);
      if (bookError) throw new Error(t('main.errors.bookNotFound'));

      setBook(loadedBook);
      setItems(itemList || []);
      setLayouts(layoutList || []);
      loadModels(pageList, layoutList, itemList, loadedBook.page_count || 0, {
        cover: {
          title: loadedBook.title || '',
          overrides: loadedBook.cover_overrides && typeof loadedBook.cover_overrides === 'object' ? loadedBook.cover_overrides : {}
        }
      });
      previewLoader.invalidate('all');

      if (!loadedBook.page_count) {
        setError(t('main.errors.noPageCount'));
      }
    } catch (err) {
      setError(friendlyMessage(err, t('main.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Le controle qualite depend du CONTENU des pages ET du format d'impression
  // (les cadres changent de taille en mm, donc la definition requise aussi).
  // Il est donc relance a chaque changement de pages plutot qu'une seule fois
  // au chargement — sinon la pastille resterait sur une page qu'on vient de
  // corriger, ou manquerait sur une photo qu'on vient de poser.
  //
  // `pages.length` et non `pages` : l'objet change d'identite a chaque
  // sauvegarde automatique, ce qui relancerait un appel reseau a chaque
  // frappe. Un rafraichissement explicite est declenche a l'ouverture de
  // "Terminer mon livre", la ou le chiffre doit etre exact.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshQualityWarnings(); }, [refreshQualityWarnings, book?.print_format]);

  useEffect(() => { refreshSnapshot(); }, [refreshSnapshot]);

  // Prix discret (§6) : recalcule des que le format ou la pagination change
  // — jamais un second calcul local, toujours estimatePrice (deja utilise
  // plus bas pour le debordement de generation automatique). Non bloquant :
  // un echec laisse simplement l'ancien prix affiche plutot que de casser
  // l'atelier pour un souci secondaire d'affichage.
  // Un livre dont le format est un produit du catalogue (reserve aux testeurs) : ses dimensions viennent du
  // serveur. On les charge avant de se fier aux tailles de page (sinon l'atelier retomberait sur Standard).
  // Le livre en pages (page-flip) se construit avec les tailles de page connues AU MOMENT du rendu : on
  // attend donc ces dimensions (ecran de chargement) avant de l'afficher. Un echec ou un format que le
  // serveur ne nous annonce pas (compte non autorise) ne bloque jamais : repli sur les dimensions Standard.
  const [formatsFailed, setFormatsFailed] = useState(false);
  const [, setFormatsTick] = useState(0);
  const formatsPending = Boolean(
    book?.print_format && !BUILTIN_FORMAT_IDS.includes(book.print_format) && !isCatalogFormat(book.print_format) && !formatsFailed
  );
  useEffect(() => {
    const id = book?.print_format;
    if (!id || BUILTIN_FORMAT_IDS.includes(id) || isCatalogFormat(id)) return undefined;
    let cancelled = false;
    listPrintFormats()
      .then(() => {
        if (cancelled) return;
        if (!isCatalogFormat(id)) setFormatsFailed(true);
        setFormatsTick((n) => n + 1);
      })
      .catch(() => { if (!cancelled) setFormatsFailed(true); });
    return () => { cancelled = true; };
  }, [book?.print_format]);

  useEffect(() => {
    if (!book?.id || !book?.print_format || !book?.page_count) return undefined;
    let cancelled = false;
    estimatePrice(book.id, { printFormat: book.print_format, pageCount: book.page_count, type: 'print', quantity: 1 })
      .then((result) => { if (!cancelled) setPriceInfo(result); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [book?.id, book?.print_format, book?.page_count]);

  // Efface le timeout en attente au demontage — evite un setState sur un
  // composant deja parti si l'utilisateur quitte l'atelier juste apres un
  // ajout/retrait de pages.
  useEffect(() => () => {
    if (priceDeltaTimeoutRef.current) clearTimeout(priceDeltaTimeoutRef.current);
  }, []);

  // Affiche breivement "+2 pages · +2,20 €" (ou l'equivalent pour un
  // changement de format), puis revient tout seul a l'affichage normal
  // (§7/§8/§11 : "pas besoin d'une grosse animation... puis l'interface
  // revient a son etat normal"). Reutilisable pour toute variation de prix
  // future (pages ou format), pas seulement les pages.
  const showPriceDelta = (delta) => {
    if (priceDeltaTimeoutRef.current) clearTimeout(priceDeltaTimeoutRef.current);
    setPriceDelta(delta);
    priceDeltaTimeoutRef.current = setTimeout(() => setPriceDelta(null), 2600);
  };

  // { [pageIndex]: nombre de photos signalees }. Seules les PHOTOS comptent
  // ici : le controle renvoie aussi des avertissements de texte, qui ont leur
  // propre signalement et ne parlent pas de nettete.
  const qualityWarningsByPage = useMemo(() => {
    const parPage = {};
    qualityWarnings
      .filter((entry) => entry.kind !== 'texte')
      .forEach((entry) => {
        if (entry.pageIndex == null) return;
        parPage[entry.pageIndex] = (parPage[entry.pageIndex] || 0) + 1;
      });
    return parPage;
  }, [qualityWarnings]);

  const limitsByPage = useMemo(() => {
    const parPage = {};
    qualityLimits.forEach((entry) => {
      if (entry.pageIndex == null) return;
      parPage[entry.pageIndex] = (parPage[entry.pageIndex] || 0) + 1;
    });
    return parPage;
  }, [qualityLimits]);

  const photos = useMemo(() => items.filter((item) => item.kind === 'photo'), [items]);
  // Apercu plein ecran : au zoom, l'apercu ecran (1600 px) est remplace par l'original.
  const photoUpgrades = useMemo(
    () => photos.map((photo) => ({ preview: photo.metadata && photo.metadata.previewUrl, original: photo.url })),
    [photos]
  );
  const souvenirs = useMemo(() => items.filter((item) => item.kind === 'texte'), [items]);
  const itemsById = useMemo(() => Object.fromEntries(items.map((item) => [item.id, item])), [items]);
  const layoutsById = useMemo(() => Object.fromEntries(layouts.map((layout) => [layout.id, layout])), [layouts]);
  const layoutsBySlugFull = useMemo(() => Object.fromEntries(layouts.map((layout) => [layout.slug, layout])), [layouts]);
  // Mises en page reellement disponibles en base. Tant que la liste n'est pas
  // chargee, on ne filtre rien (undefined) : mieux vaut tout proposer une
  // fraction de seconde que faire clignoter le panneau.
  const availableLayoutSlugs = useMemo(
    () => (layouts.length > 0 ? new Set(layouts.map((layout) => layout.slug)) : undefined),
    [layouts]
  );

  // Elements deja places sur UNE page interieure quelconque du livre (pas
  // seulement la page en cours) — retour utilisateur : eviter les doublons
  // accidentels en glissant deux fois la meme photo sans s'en rendre compte.
  // Union de toutes les pages deja sauvegardees (pages[].content.itemIds) ET
  // du brouillon de la page en cours (draftSlotItemIds) : ce dernier peut
  // contenir un ajout tout juste depose, pas encore reflete dans `pages`
  // (la sauvegarde auto est asynchrone) — sans lui, le badge "utilisee"
  // manquerait pendant l'instant qui suit un depot. Volontairement
  // informatif, jamais bloquant : reutiliser sciemment une meme photo (ex.
  // avant/apres) reste possible.
  const usedItemIds = useMemo(() => {
    const ids = new Set();
    Object.values(bookState.pages).forEach((model) => {
      itemIdsOfModel(model).forEach((id) => ids.add(id));
    });
    return ids;
  }, [bookState.pages]);

  const totalPages = book?.page_count || 0;
  // Un vis-a-vis de plus qu'avant : la page 1 est seule a droite, elle
  // occupe donc sa propre vue (voir utils/pageParity.js).
  const interiorSpreadCount = Math.max(1, spreadCount(totalPages));
  const totalViews = interiorSpreadCount + 2; // couverture + double-pages + 4e

  // Verification affichee au clic sur "Terminer mon livre" (AtelierFinishModal)
  // — a partir des donnees deja chargees, aucun nouvel appel reseau. "Page
  // complete" reprend exactement le meme critere que la sauvegarde auto de
  // l'atelier (voir plus bas : isComplete = tous les emplacements remplis).
  // Une page jamais construite (aucune ligne dans book_pages) compte comme
  // incomplete, meme logique que le rendu (routes/composition.js: une page
  // absente se rend comme une page vide plutot qu'une 404).
  // pageStatuses (meme passage) alimente aussi le filmstrip de pages
  // ci-dessous — un seul parcours de `pages`, pas une deuxieme boucle
  // dupliquee pour un besoin d'affichage tres proche. `preview` (2026-10-09,
  // vraies miniatures du filmstrip) suit le meme principe : aucun appel
  // reseau ici, uniquement des URLs deja en memoire (metadata.thumbnailUrl,
  // le JPEG 480px genere a l'upload — voir storageService.js) et la
  // geometrie en % de la mise en page (atelierLayoutGeometry.js, deja
  // utilisee pour l'incrustation de glisser-deposer sur la page reelle).
  const finishStats = useMemo(() => {
    let incompletePages = 0;
    let pagesCreated = 0;
    const pageStatuses = [];
    for (let index = 0; index < totalPages; index += 1) {
      const info = summarizePage(bookState.pages[index], layoutsById);
      if (info.hasRow) pagesCreated += 1;
      const layout = info.slug ? layoutsBySlugFull[info.slug] : null;
      // Pages "sans emplacement" (phase31 : dedicace/chapitre manuel/
      // remerciements, voir manual-kind cote serveur) — aucun layout_id ni
      // itemIds a compter, leur propre notion de "rempli" vit dans
      // title/text/photoItemId. Sans ce branchement, slotCount valait
      // toujours 0 (layout=null) et la page comptait donc TOUJOURS comme
      // incomplete dans "pages a completer", meme pleinement redigee — meme
      // risque deja identifie cote serveur (isPageEmpty, bookContentService.js).
      const pageKind = info.kind;
      let slotCount = 0;
      let filledCount = 0;
      let status;
      if (pageKind === 'dedication' || pageKind === 'thank-you') {
        const filled = Boolean(String(info.text || '').trim());
        status = filled ? 'complete' : 'empty';
        if (!filled) incompletePages += 1;
      } else if (pageKind === 'chapter-divider-manuel') {
        const filled = Boolean(info.photoItemId) || Boolean(String(info.title || '').trim());
        status = filled ? 'complete' : 'empty';
        if (!filled) incompletePages += 1;
      } else {
        slotCount = layout?.capacity?.slots?.length || 0;
        filledCount = info.itemIds.filter(Boolean).length;
        if (slotCount === 0 || filledCount !== slotCount) incompletePages += 1;
        status = slotCount > 0 && filledCount === slotCount
          ? 'complete'
          : filledCount > 0
            ? 'partial'
            : 'empty';
      }

      const slug = layout?.slug || null;
      const atelierLayout = slug ? findAtelierLayout(slug) : null;
      const geometry = slug ? getOverlayGeometry(slug) : null;
      const itemIds = info.itemIds;
      // Double page (FULL_PHOTO_SPREAD) : la MEME photo est enregistree sur
      // les deux pages (voir pageRenderer.js/.photo-spread cote serveur) —
      // ici on ne fait que reproduire en CSS le meme recadrage gauche/droite
      // (background-size 200% + position left/right), jamais une deuxieme
      // source de verite sur quelle moitie afficher.
      const isSpread = atelierLayout?.spread === true;
      const slots = geometry && atelierLayout
        ? geometry.map((rect, slotIndex) => {
          const kind = atelierLayout.slots[slotIndex];
          const item = itemIds[slotIndex] ? itemsById[itemIds[slotIndex]] : null;
          return {
            rect,
            kind,
            thumbnailUrl: item?.kind === 'photo' ? (item.metadata?.thumbnailUrl || item.url) : null
          };
        })
        : null;
      const preview = slots ? { slots, isSpread, spreadSide: isSpread ? (isLeftPage(index) ? 'left' : 'right') : null } : null;

      pageStatuses.push({ pageIndex: index, status, preview });
    }
    return {
      photosCount: photos.length,
      souvenirsCount: souvenirs.length,
      pagesCreated,
      incompletePages,
      pageStatuses
    };
  }, [totalPages, bookState.pages, layoutsById, layoutsBySlugFull, itemsById, photos.length, souvenirs.length]);
  const lastViewIndex = totalViews - 1;

  // Lien profond "?page=N" — utilise par "Revoir ces pages" depuis l'ecran
  // recapitulatif qualite avant commande (PrintQualityRecapModal). Ne
  // s'applique qu'UNE fois, au premier chargement des pages : ensuite
  // l'utilisateur navigue normalement (sinon chaque rendu le ramenerait de
  // force sur la meme page).
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (deepLinkAppliedRef.current || !book?.page_count) return;
    // Sans parametre "page" du tout, searchParams.get() renvoie null, et
    // Number(null) vaut 0 (pas NaN) : sans ce garde explicite, CHAQUE
    // premier chargement (aucun "?page=" dans l'URL) etait pris pour un lien
    // profond vers la page 0 et ecrasait l'atterrissage sur la couverture
    // (retour utilisateur, 2026-10-04 : "il faut arriver sur la couverture
    // et non la premiere page") — invisible jusque-la seulement parce que
    // l'ancienne valeur par defaut de viewIndex (1) coincidait par hasard
    // avec ce que ce calcul produisait.
    const rawParam = searchParams.get('page');
    if (rawParam === null) return;
    const raw = Number(rawParam);
    if (!Number.isInteger(raw) || raw < 0 || raw >= book.page_count) return;
    deepLinkAppliedRef.current = true;
    setViewIndex(spreadOfPage(raw) + 1);
    setSelectedSide(isLeftPage(raw) ? 'left' : 'right');
  }, [book?.page_count, searchParams]);

  const viewKind = viewIndex === 0 ? 'cover' : viewIndex === lastViewIndex ? 'back-cover' : 'spread';
  const spreadNumber = viewKind === 'spread' ? viewIndex - 1 : null;
  // LE PREMIER VIS-A-VIS N'A PAS DE PAGE DE GAUCHE.
  //
  // La page 1 d'un livre relie est seule a droite, face au contre-plat. On
  // l'affiche donc telle quelle, plutot que de la coller a la page 2 —
  // c'est ce mensonge qui a fait sortir une double page en recto-verso sur
  // le livre imprime du 2026-09-25 (voir utils/pageParity.js).
  const paire = spreadNumber != null ? spreadPair(spreadNumber) : null;
  const leftPageIndex = paire && paire.left != null && paire.left < totalPages ? paire.left : null;
  const rightPageIndex = paire && paire.right != null && paire.right < totalPages ? paire.right : null;

  // Le cote choisi peut ne pas exister : au premier vis-a-vis il n'y a pas
  // de page de gauche, au dernier il peut n'y avoir pas de page de droite.
  // On retombe alors sur le cote qui existe, plutot que sur rien — sinon
  // l'atelier s'ouvrait sur une page vide qu'on ne pouvait pas composer.
  const currentPageIndex = viewKind === 'spread'
    ? (selectedSide === 'right'
      ? (rightPageIndex != null ? rightPageIndex : leftPageIndex)
      : (leftPageIndex != null ? leftPageIndex : rightPageIndex))
    : null;

  // MODELE EDITABLE de la page courante (store) et les valeurs "draft*" qui en
  // sont deduites. Rien n'est copie : modifier une page = dispatch d'une action
  // sur le store (voir editPage plus bas), et tout ce qui l'affiche (page,
  // panneau, pellicule) se met a jour depuis cette meme source.
  const pageModel = getPageModel(bookState, currentPageIndex);
  // Une photo etalee sur double page est-elle visible dans la vue courante ?
  const viewHasSpreadPhoto = viewKind === 'spread' && [leftPageIndex, rightPageIndex].some(
    (index) => index != null && bookState.pages[index] && bookState.pages[index].layoutSlug === 'FULL_PHOTO_SPREAD'
  );
  const draftLayoutSlug = pageModel ? pageModel.layoutSlug : null;
  const draftSlotItemIds = pageModel ? pageModel.slotItemIds : EMPTY_ARRAY;
  const draftPageKind = pageModel ? pageModel.kind : null;
  const draftPageKindFields = pageModel ? pageModel.kindFields : EMPTY_KIND_FIELDS;
  const draftSlotIds = pageModel ? pageModel.slotIds : EMPTY_ARRAY;
  const draftFraming = pageModel ? pageModel.framing : EMPTY_OBJECT;
  const selectedSlotIndex = selectedFraming && selectedFraming.type === 'slot' && selectedFraming.pageIndex === currentPageIndex
    ? selectedFraming.slotIndex : null;
  const selectedCoverFace = selectedFraming && selectedFraming.type === 'cover' ? selectedFraming.face : null;
  const draftPhotoCaptions = pageModel ? pageModel.photoCaptions : EMPTY_OBJECT;
  const draftTextRoles = pageModel ? pageModel.textRoles : EMPTY_OBJECT;
  const draftTextStyles = pageModel ? pageModel.textStyles : EMPTY_OBJECT;

  // Changer de page referme ce qui etait ouvert sur la precedente.
  useEffect(() => {
    setSelectedFraming(null);
    setEditingTextSlot(null);
    setSelectedSidebarItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex]);

  // Une photo selectionnee ne le reste pas quand on change de vue (couverture, 4e, autre double page).
  useEffect(() => { setSelectedFraming(null); }, [viewIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // CE QUE L'ECRAN VEUT VOIR : la vue courante, puis ses voisines (le
  // feuilletage a besoin des pages d'a cote pour animer le feuillet). Le
  // chargeur ne demande que ca, 4 requetes au plus a la fois.
  useEffect(() => {
    if (!totalPages) return;
    const keys = [];
    const pushView = (view) => {
      if (view === 0) { keys.push(COVER_KEYS.front); return; }
      if (view === lastViewIndex) { keys.push(COVER_KEYS.back); return; }
      const pair = spreadPair(view - 1);
      [pair.left, pair.right].forEach((index) => { if (index != null && index < totalPages) keys.push(pageKey(index)); });
    };
    [0, 1, -1, 2, -2].forEach((delta) => {
      const view = viewIndex + delta;
      if (view >= 0 && view <= lastViewIndex) pushView(view);
    });
    previewLoader.want('livre', keys, 0);
  }, [viewIndex, totalPages, lastViewIndex, previewLoader]);

  // Apercu au survol d'une vignette du filmstrip (2026-10-10, retour
  // utilisateur : "comme on fait avec les photos" — voir AtelierSidebar,
  // meme principe de survol differe). Reutilise TEL QUEL le meme cache que
  // la vue centrale (pagePreviewCache/coverHtml/backCoverHtml) : survoler une
  // page deja vue au centre est instantane et gratuit, et une page jamais
  // vue ne coute qu'UN appel, au moment reel du survol — jamais prechargee
  // pour les dizaines de pages non survolees.
  const filmstripKey = (target) => (target === 'cover' ? COVER_KEYS.front : target === 'back-cover' ? COVER_KEYS.back : pageKey(target));
  // Vignettes visibles dans la bande : le chargeur ne demande que celles-la,
  // APRES ce que le livre ouvert veut voir (priorite 1 contre 0).
  const handleFilmstripVisible = useCallback((targets) => {
    previewLoader.want('bande', targets.map(filmstripKey), 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLoader]);

  const requestFilmstripPreview = useCallback((target) => {
    const key = target === 'cover' ? COVER_KEYS.front : target === 'back-cover' ? COVER_KEYS.back : pageKey(target);
    return previewLoader.request(key);
  }, [previewLoader]);

  // --- Photo sur double page -------------------------------------------
  // Les deux pages d'une double page sont celles qui se FONT FACE dans le
  // livre relie : (1,2), (3,4), (5,6)... La page 1 n'a personne en face
  // d'elle et ne peut donc pas porter de double page (voir
  // utils/pageParity.js, et le livre imprime qui l'a etabli).
  const isSpreadLayout = (slug) => findAtelierLayout(slug)?.spread === true;

  // Toutes les pages ne peuvent pas porter une double page : il faut une page
  // EN FACE. La page 1 n'en a pas (elle est seule a droite), et la derniere
  // page non plus si elle tombe a gauche. Proposer quand meme le format
  // donnait une demi-photo sans sa moitie — c'est ce qui s'est imprime.
  const doublePagePossible = (() => {
    if (currentPageIndex == null) return false;
    const enFace = facingPageIndex(currentPageIndex);
    return enFace != null && enFace < totalPages;
  })();

  // La page porte-t-elle une double page ? On interroge le modele de la page
  // (jamais la galerie ouverte par-dessus). La page jumelle est tenue a jour
  // dans la MEME action que la page courante (voir bookReducer.spreadSibling) :
  // plus de mirrorSpread/releaseSpreadSibling asynchrones greffes sur la
  // sauvegarde.
  const savedPageIsSpread = (index) => {
    const model = bookState.pages[index];
    return Boolean(model && !model.kind && model.layoutSlug && isSpreadLayout(model.layoutSlug));
  };

  // UNIQUE porte d'entree pour modifier la page courante. `meta` : label du
  // geste (ecrit dans l'historique et dans le message "Annuler"), toast (le
  // message est-il affiche ?), coalesceKey (les gestes continus d'une meme
  // nature ne forment qu'un seul pas), fast (geste ponctuel : ecriture plus
  // rapide qu'une frappe — voir store/saveEngine.js).
  const editPage = (patch, meta = {}) => {
    if (currentPageIndex == null) return;
    bookDispatch({ type: BOOK_ACTIONS.EDIT_PAGE, index: currentPageIndex, patch, meta: { fast: true, ...meta } });
  };

  const handleChooseLayout = (slug) => {
    const atelierLayout = findAtelierLayout(slug);
    if (!atelierLayout) return;
    // Le message "Annuler" n'a de sens que s'il y avait quelque chose a perdre.
    const hadContent = isManualPage(pageModel);
    editPage({
      kind: null,
      kindFields: { ...EMPTY_KIND_FIELDS },
      layoutSlug: slug,
      slotItemIds: new Array(atelierLayout.slots.length).fill(null),
      // Nouveaux emplacements = nouveaux identifiants, cadrage par defaut (la
      // photo, elle, est gardee quand on la replace).
      slotIds: newSlotIds(atelierLayout.slots.length),
      framing: {},
      textRoles: {},
      textStyles: {}
      // Les legendes SURVIVENT : elles decrivent la photo, pas le cadre.
    }, { label: t('store.toasts.layoutChanged'), toast: hadContent });
  };

  // Choisit une page "sans emplacement" (phase31) — mutuellement exclusif
  // avec handleChooseLayout : repart d'un modele vide plutot que de melanger
  // les deux systemes.
  const handleChoosePageKind = (kind) => {
    if (!ATELIER_PAGE_KIND_VALUES.has(kind)) return;
    const hadContent = isManualPage(pageModel);
    editPage({
      layoutSlug: null,
      slotItemIds: [],
      slotIds: [],
      framing: {},
      photoCaptions: {},
      textRoles: {},
      textStyles: {},
      kind,
      kindFields: { ...EMPTY_KIND_FIELDS }
    }, { label: t('store.toasts.layoutChanged'), toast: hadContent });
  };

  const handlePageKindFieldChange = (field, value) => {
    // Une frappe n'est enregistree qu'apres un court silence, et toutes les
    // touches d'un meme champ ne forment qu'UN pas d'historique.
    editPage((model) => ({ kindFields: { ...model.kindFields, [field]: value } }), {
      coalesceKey: `kind:${field}`,
      fast: field === 'photoItemId'
    });
  };

  const handleAssignSlot = (slotIndex, itemId) => {
    // REMPLACEMENT d'un emplacement deja occupe : c'est une perte, au meme
    // titre qu'un changement de mise en page — d'ou le message "Annuler".
    // Poser un element dans un emplacement VIDE n'efface rien : pas de message.
    const previousId = draftSlotItemIds[slotIndex];
    const replaced = Boolean(previousId) && previousId !== itemId;
    const label = replaced
      ? (itemsById[previousId]?.kind === 'texte' ? t('store.toasts.memoryReplaced') : t('store.toasts.photoReplaced'))
      : t('store.toasts.photoPlaced');
    editPage((model) => {
      const next = [...model.slotItemIds];
      next[slotIndex] = itemId;
      const patch = { slotItemIds: next };
      // Une AUTRE photo dans l'emplacement : cadrage par defaut (jamais le cadrage
      // de la photo precedente, qui n'a pas de sens pour celle-ci).
      const slotId = model.slotIds[slotIndex];
      if (slotId && model.framing[slotId] && previousId !== itemId) {
        const framing = { ...model.framing };
        delete framing[slotId];
        patch.framing = framing;
      }
      return patch;
    }, { label, toast: replaced });
    setSelectedSidebarItem(null);
  };

  // Echange de deux emplacements de la page (glisser une photo sur une autre).
  // Chaque photo emporte sa legende (elle est liee a la photo) ; le CADRAGE,
  // lui, appartient a l'emplacement et repart au defaut pour les deux (une autre
  // photo dans le cadre : son ancien cadrage n'a plus de sens). Deposer sur un
  // emplacement vide DEPLACE la photo. Refuse si un type ne convient pas.
  const handleSwapSlots = (fromIndex, toIndex) => {
    const layout = draftLayoutSlug ? findAtelierLayout(draftLayoutSlug) : null;
    if (!layout || fromIndex === toIndex) return;
    const first = draftSlotItemIds[fromIndex] ? itemsById[draftSlotItemIds[fromIndex]] : null;
    const second = draftSlotItemIds[toIndex] ? itemsById[draftSlotItemIds[toIndex]] : null;
    if (!slotAcceptsItem(layout.slots[toIndex], first) || !slotAcceptsItem(layout.slots[fromIndex], second)) return;
    editPage((model) => {
      const next = [...model.slotItemIds];
      [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
      const framing = { ...model.framing };
      delete framing[model.slotIds[fromIndex]];
      delete framing[model.slotIds[toIndex]];
      return { slotItemIds: next, framing };
    }, { label: second ? t('store.toasts.photosSwapped') : t('store.toasts.photoMoved'), toast: true });
    setSelectedFraming(null);
  };

  const handleRemoveSlot = (slotIndex) => {
    const removedItemId = draftSlotItemIds[slotIndex];
    const removed = removedItemId ? itemsById[removedItemId] : null;
    editPage((model) => {
      const next = [...model.slotItemIds];
      next[slotIndex] = null;
      const patch = { slotItemIds: next };
      // Le cadrage appartient a l'EMPLACEMENT : un emplacement vide n'en garde pas.
      const slotId = model.slotIds[slotIndex];
      if (slotId && model.framing[slotId]) {
        const framing = { ...model.framing };
        delete framing[slotId];
        patch.framing = framing;
      }
      if (removedItemId && model.photoCaptions[removedItemId]) {
        const captions = { ...model.photoCaptions };
        delete captions[removedItemId];
        patch.photoCaptions = captions;
      }
      return patch;
    }, {
      label: removed?.kind === 'texte' ? t('store.toasts.memoryRemoved') : t('store.toasts.photoRemoved'),
      toast: Boolean(removedItemId)
    });
  };

  // Choisir/importer une photo pour un emplacement VIDE (retour
  // utilisateur, 2026-09-25) — ouvert au clic direct sur le cadre, voir
  // AtelierPageOverlay.js/AtelierPhotoPickerModal.js.
  const photosNonUtilisees = useMemo(
    () => photos.filter((photo) => !usedItemIds.has(photo.id)),
    [photos, usedItemIds]
  );

  // BIBLIOTHEQUE VIDE : cliquer un emplacement photo ouvre DIRECTEMENT le choix de fichier (retour utilisateur,
  // 2026-10-17 : « il faut pouvoir ajouter une photo en cliquant sur l'emplacement photo dans la couverture,
  // ouvrir les fichiers si aucune photo importee »). Avec au moins une photo importee, on garde le panneau de
  // choix (bibliotheque + « Importer une photo »). Le fichier choisi est depose ET place dans l'emplacement
  // clique (handleUploadPhotoForPicker) ; annuler le dialogue referme le choix.
  const directFileInputRef = useRef(null);
  const openFileDialogIfLibraryEmpty = () => {
    if (photos.length > 0) return;
    const input = directFileInputRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  };
  const bindDirectFileInput = (element) => {
    directFileInputRef.current = element;
    if (element && !element.dataset.cancelBound) {
      element.dataset.cancelBound = '1';
      // Evenement « cancel » des champs fichier (navigateurs recents) : dialogue ferme sans fichier.
      element.addEventListener('cancel', () => { handleClosePhotoPicker(); });
    }
  };
  const handleDirectFileChosen = (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (file) handleUploadPhotoForPicker(file);
    else handleClosePhotoPicker();
  };

  const handleOpenPhotoPicker = (slotIndex) => {
    setPickerUploadError('');
    setPickerTargetCoverFace(null);
    setPickerTargetSlotIndex(slotIndex);
    openFileDialogIfLibraryEmpty();
  };

  // Meme fenetre, pour la couverture/4e (retour utilisateur, 2026-09-26) —
  // voir CoverPhotoOverlay dans AtelierBookView.js, ouvert au clic direct
  // sur un cadre de couverture vide.
  // `slot` : emplacement d'une couverture en MOSAIQUE (0 = grande photo) ; null = la photo unique de la face.
  const [coverPickerSlot, setCoverPickerSlot] = useState(null);
  const handleOpenCoverPhotoPicker = (face, slot = null) => {
    setPickerUploadError('');
    setPickerTargetSlotIndex(null);
    setCoverPickerSlot(typeof slot === 'number' ? slot : null);
    setPickerTargetCoverFace(face);
    openFileDialogIfLibraryEmpty();
  };

  // Pose UNE photo dans un emplacement de la mosaique, sans toucher aux autres choix.
  const handleAssignCoverSlot = (slot, itemId) => {
    const current = Array.isArray(coverOverridesNow.frontPhotoIds) ? [...coverOverridesNow.frontPhotoIds] : [];
    while (current.length < 3) current.push(null);
    current[slot] = itemId;
    editCover({ overrides: { frontPhotoIds: current } }, { label: t('store.toasts.coverUpdated'), toast: true });
    setSelectedFraming(null);
    setSelectedSidebarItem(null);
  };

  // Afficher le panneau « Mise en page / Style » : referme tout ce qui le masquait (choix de photo, photo selectionnee).
  const openLayoutPanel = () => {
    setPickerTargetSlotIndex(null);
    setPickerTargetCoverFace(null);
    setCoverPickerSlot(null);
    setSelectedFraming(null);
    setActiveDrawer('layout');
  };

  const handleClosePhotoPicker = () => {
    setPickerTargetSlotIndex(null);
    setPickerTargetCoverFace(null);
    setCoverPickerSlot(null);
    setPickerUploadError('');
  };

  // Quand le choix de photo monte en feuille (ecran etroit, zoom navigateur, mode Agrandir), on amene l'emplacement vise
  // au-dessus d'elle ; le scroll-padding CSS reserve la place de la feuille (retour 2026-10-21).
  useEffect(() => {
    if (pickerTargetSlotIndex == null && !pickerTargetCoverFace) return undefined;
    const timer = setTimeout(() => {
      const cible = document.querySelector('.atelier-overlay-slot.is-pick-target, .atelier-cover-photo-overlay.is-pick-target');
      if (cible && typeof cible.scrollIntoView === 'function') cible.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 80);
    return () => clearTimeout(timer);
  }, [pickerTargetSlotIndex, pickerTargetCoverFace]);

  // Apres avoir pose une photo, le choix passe tout seul a l'emplacement PHOTO vide suivant (celui d'apres, puis on
  // reprend au debut) ; il se ferme quand il n'en reste plus (correctif 4 : 4 emplacements = 4 clics).
  const advancePickerAfterPlacing = (placedSlotIndex, itemId) => {
    const slots = (draftLayoutForOverlay && draftLayoutForOverlay.slots) || [];
    const filled = [...draftSlotItemIds];
    filled[placedSlotIndex] = itemId;
    const order = [...slots.keys()].filter((i) => i !== placedSlotIndex);
    const after = order.filter((i) => i > placedSlotIndex);
    const before = order.filter((i) => i < placedSlotIndex);
    const next = [...after, ...before].find((i) => slots[i] === 'photo' && !filled[i]);
    if (next === undefined) { handleClosePhotoPicker(); return; }
    setPickerUploadError('');
    setPickerTargetSlotIndex(next);
  };

  const handlePickPhotoFromLibrary = (itemId) => {
    if (pickerTargetCoverFace) {
      if (coverPickerSlot != null) handleAssignCoverSlot(coverPickerSlot, itemId);
      else handleAssignCoverPhoto(pickerTargetCoverFace, itemId);
      handleClosePhotoPicker();
      return;
    }
    if (pickerTargetSlotIndex == null) return;
    // -1 : la photo d'une page de CHAPITRE (champ photoItemId), pas un emplacement de mise en page.
    if (pickerTargetSlotIndex === -1) { handlePageKindFieldChange('photoItemId', itemId); handleClosePhotoPicker(); return; }
    handleAssignSlot(pickerTargetSlotIndex, itemId);
    advancePickerAfterPlacing(pickerTargetSlotIndex, itemId);
  };

  // Import direct depuis le disque, POUR CET EMPLACEMENT PRECIS (page OU
  // couverture/4e) : un seul fichier, qui remplit la cible des que l'envoi
  // reussit — distinct de envoyerLesPhotos (lot vers la bibliotheque
  // generale, avec detection de doublons) : ici le geste est deliberement
  // cible sur un cadre precis, la detection de doublons du lot n'a pas sa
  // place dans ce chemin court.
  const handleUploadPhotoForPicker = async (file) => {
    if (!book?.id || (pickerTargetSlotIndex == null && !pickerTargetCoverFace)) return;
    setPickerUploading(true);
    setPickerUploadError('');
    try {
      const created = await uploadPhoto(book.id, file, items.length);
      setItems((previous) => [...previous, created]);
      if (pickerTargetCoverFace) {
        if (coverPickerSlot != null) handleAssignCoverSlot(coverPickerSlot, created.id);
        else handleAssignCoverPhoto(pickerTargetCoverFace, created.id);
      } else {
        if (pickerTargetSlotIndex === -1) handlePageKindFieldChange('photoItemId', created.id);
        else {
          handleAssignSlot(pickerTargetSlotIndex, created.id);
          advancePickerAfterPlacing(pickerTargetSlotIndex, created.id);
          return;
        }
      }
      handleClosePhotoPicker();
    } catch (err) {
      // Photo trop petite : le message du serveur est precis (dimensions, minimum attendu) et deja dans la bonne langue.
      setPickerUploadError(err && err.code === 'photo_too_small' ? err.message : friendlyMessage(err, t('main.errors.photoUploadFailed')));
    } finally {
      setPickerUploading(false);
    }
  };

  // L'ecran recapitulatif ("Terminer mon livre" > "Revoir ces pages") demande de
  // SELECTIONNER une photo precise : une fois la bonne page affichee, on la
  // selectionne (barre flottante), plus aucune fenetre.
  useEffect(() => {
    if (!pendingAdjustItemId) return;
    const slotIndex = draftSlotItemIds.indexOf(pendingAdjustItemId);
    if (slotIndex !== -1 && currentPageIndex != null) {
      setSelectedFraming({ type: 'slot', pageIndex: currentPageIndex, slotIndex });
    }
    setPendingAdjustItemId(null);
  }, [pendingAdjustItemId, currentPageIndex, draftSlotItemIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sauvegarde d'un texte modifie EN PLACE sur la page (cahier des charges
  // typographique §1, voir AtelierTextEditor.js).
  //
  // Deux choses distinctes a enregistrer, volontairement separees :
  //   - le TEXTE appartient au souvenir (content_items), il est partage par
  //     toutes les pages qui l'utilisent -> updateContentItem ;
  //   - le ROLE et les reglages appartiennent a CETTE page (c'est une
  //     decision de mise en page, pas une propriete du souvenir) -> etat
  //     local, persiste par l'autosauvegarde existante.
  // Les confondre ferait qu'un meme souvenir repris ailleurs imposerait son
  // role a l'autre page.
  // Suite d'un texte sur la page suivante : on va a cette page ; si elle est vide, elle prend la mise en page « texte seul »
  // et recoit soit le surplus colle, soit un editeur vide pret a ecrire. Si elle contient deja quelque chose, on ne
  // l'ecrase JAMAIS : simple message.
  useEffect(() => {
    if (!continuation || continuation.navigated) return;
    setContinuation({ ...continuation, navigated: true });
    goToFilmstripTarget(continuation.pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuation]);

  useEffect(() => {
    if (!continuation || !continuation.navigated || continuation.pageIndex !== currentPageIndex || viewKind !== 'spread') return;
    const target = continuation;
    setContinuation(null);
    const empty = !(pageModel.slotItemIds || []).some(Boolean) && !pageModel.kind;
    if (!empty) { pushToast({ id: 'continue-text', message: t('main.errors.nextPageNotEmpty') }); return; }
    handleChooseLayout('ONE_TESTIMONY');
    // On attend que la page precedente soit ENREGISTREE : un texte cree entre-temps (origine « page ») serait
    // balaye comme orphelin par son enregistrement, avant d'avoir ete pose sur sa page.
    setTimeout(async () => {
      await ensureSaved();
      if (target.text) handleCreateText(0, { text: target.text, role: 'quote', styleOverrides: {} });
      else setEditingTextSlot(0);
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuation, currentPageIndex, viewKind]);

  const handleSaveText = async (itemId, { text, role, styleOverrides }) => {
    // Role et reglages : dans le modele de la page (enregistres et annulables).
    editPage((model) => ({
      textRoles: { ...model.textRoles, [itemId]: role },
      textStyles: { ...model.textStyles, [itemId]: styleOverrides || {} }
    }), { label: t('store.toasts.textEdited'), coalesceKey: `text:${itemId}`, fast: true });

    const current = itemsById[itemId];
    const nextText = String(text ?? '');
    if (!current || current.text === nextText) return;

    // Mise a jour optimiste : l'apercu doit refleter la frappe immediatement,
    // pas apres un aller-retour reseau.
    setItems((previous) => previous.map((item) => (
      item.id === itemId ? { ...item, text: nextText } : item
    )));
    try {
      await updateContentItem(book.id, itemId, { text: nextText });
      // RECHARGE l'apercu de la page : le texte est bien enregistre en base, il
      // faut que l'utilisateur voie la nouvelle version (retour 2026-09-11 :
      // "j'ai saisi du texte dans le cadre mais il s'enregistre pas").
      await refreshPagePreview(currentPageIndex);
    } catch (_err) {
      // On remet la valeur d'origine : laisser un texte a l'ecran que le
      // serveur n'a pas enregistre serait le pire des deux mondes. Message
      // simple, jamais l'erreur brute du serveur.
      setItems((previous) => previous.map((item) => (
        item.id === itemId ? { ...item, text: current.text } : item
      )));
      pushToast({ id: 'text-error', message: t('main.errors.textUpdateFailed') });
    }
  };

  // Ecriture DIRECTE dans un emplacement vide (retour utilisateur
  // 2026-09-11 : "il faut pouvoir ecrire directement dans les cases vierges
  // ou bien glisser un souvenir"). Le souvenir est cree a la volee puis pose
  // dans l'emplacement — il rejoint donc "Mes souvenirs" comme n'importe
  // quel autre, plutot que de vivre uniquement dans cette page.
  //
  // Rien n'est cree si le champ est laisse vide : ouvrir un emplacement puis
  // se raviser ne doit pas polluer la liste des souvenirs.
  const handleCreateText = async (slotIndex, { text, role, styleOverrides }) => {
    const value = String(text || '').trim();
    if (!book?.id || !value) return;

    try {
      // 'page' : ce souvenir n'existe que pour l'emplacement ou il vient
      // d'etre ecrit. S'il en sort (retire, mise en page changee, page videe),
      // le serveur le supprime au lieu de le laisser encombrer la
      // bibliotheque — celle-ci sert d'abord aux souvenirs des contributeurs
      // (regle produit 2026-09-14).
      const created = await addTextItem(book.id, value, items.length, 'page');
      setItems((previous) => [...previous, created]);
      // UN seul pas : le souvenir, son role et ses reglages arrivent ensemble.
      editPage((model) => {
        const next = [...model.slotItemIds];
        next[slotIndex] = created.id;
        return {
          slotItemIds: next,
          textRoles: { ...model.textRoles, [created.id]: role },
          textStyles: { ...model.textStyles, [created.id]: styleOverrides || {} }
        };
      }, { label: t('store.toasts.memoryPlaced') });
      setSelectedSidebarItem(null);
    } catch (_err) {
      pushToast({ id: 'text-error', message: t('main.errors.textCreateFailed') });
    }
  };

  // Photo REELLEMENT affichee sur la face courante. On ne la redevine pas :
  // le choix automatique est pris par le serveur (coverComposer), et une
  // surcharge explicite (cover_overrides) peut ne plus correspondre a rien
  // (photo supprimee depuis). On lit donc l'apercu deja rendu, qui est la
  // verite affichee — meme source que ce qui sera imprime.
  const coverPhotoItem = useMemo(() => {
    if (viewKind !== 'cover' && viewKind !== 'back-cover') return null;
    const html = viewKind === 'cover' ? coverHtml : backCoverHtml;
    if (!html) return null;
    // L'apercu est du HTML complet : on y retrouve l'URL de la photo posee.
    // Le rendu embarque metadata.previewUrl (variante redimensionnee pour
    // l'ecran, voir AtelierSidebar.js/AtelierPhotoAdjustModal.js — meme
    // convention partout dans l'atelier), PAS photo.url (l'original) : sans
    // ce second essai, une photo de couverture fraichement assignee n'etait
    // jamais detectee (bug trouve le 2026-09-26 en testant le nouveau
    // "Retirer" ci-dessous — le bouton n'apparaissait jamais).
    const trouve = photos.find((photo) => (
      (photo.url && html.includes(photo.url))
      || (photo.metadata?.previewUrl && html.includes(photo.metadata.previewUrl))
    ));
    return trouve || null;
  }, [viewKind, coverHtml, backCoverHtml, photos]);

  // Le gabarit choisi contient-il une photo, pour chaque face (retour
  // utilisateur, 2026-09-26 : "si le template contient une photo") — MIROIR
  // de showPhotoPicker/showBackPhotoPicker dans AtelierCoverPanel.js (meme
  // convention de duplication assumee que le reste de ce fichier). Determine
  // si CoverPhotoOverlay reagit au clic ou reste invisible (voir
  // AtelierBookView.js).
  const frontHasPhotoSlot = (book?.cover_overrides?.frontVariant || 'AUTO') !== 'COVER_MINIMAL';
  const backCoverVariant = book?.cover_overrides?.backVariant || 'AUTO';
  const backHasPhotoSlot = backCoverVariant === 'AUTO' || backCoverVariant === 'BACK_PHOTO_STATS';

  // Cadrage de chaque face (cover_overrides.frontPhotoAdjust / backPhotoAdjust,
  // ancien format { focalX, focalY, zoom, fitMode } CONSERVE pour la couverture) :
  // converti vers le cadrage d'emplacement { fit, focusX, focusY, zoom } pour
  // l'affichage, et reconverti a l'enregistrement. Aucune migration.
  const coverFramingOf = (face) => framingFromAdjustment(
    (book?.cover_overrides || {})[face === 'front' ? 'frontPhotoAdjust' : 'backPhotoAdjust']
  );

  // UNE porte d'entree pour modifier la couverture/4e : meme historique, meme
  // moteur de sauvegarde que les pages (plus de panneau qui ecrit de son cote).
  const editCover = (patch, meta = {}) => {
    bookDispatch({ type: BOOK_ACTIONS.EDIT_COVER, patch, meta: { fast: true, ...meta } });
  };

  // Edition directe du titre / sous-titre / date sur la couverture : meme chemin que le
  // panneau (meme historique, meme sauvegarde ; touches d'un meme champ = un seul pas).
  const handleCoverTextChange = (prop, value) => {
    editCover(coverPatchFor({ [prop]: value }), {
      label: t('store.toasts.coverUpdated'),
      coalesceKey: 'cover:' + prop,
      fast: false
    });
  };
  const coverOverridesNow = (book && book.cover_overrides && typeof book.cover_overrides === 'object') ? book.cover_overrides : {};

  // Cadrage de la photo de couverture/4e. Meme barre, memes gestes que sur une
  // page ; enregistre dans cover_overrides (ancien format, voir coverFramingOf).
  // Entiere puis Remplir : on retrouve le zoom et le point de focus d'avant (sinon un
  // simple va-et-vient entre les deux modes effacerait le cadrage soigneusement
  // regle). Memoire de session seulement, par emplacement / face de couverture.
  const fitMemoryRef = useRef({});
  const rememberFit = (key, current, patch) => {
    if (patch.fit === 'contain') {
      if (current.fit === 'cover' && !isDefaultFraming(current)) {
        fitMemoryRef.current[key] = { focusX: current.focusX, focusY: current.focusY, zoom: current.zoom };
      }
      // "Entiere" repart CENTREE (sinon le decalage fait en mode Remplir collait la
      // photo entiere contre un bord : moitie de double page vide) et sans zoom.
      return { ...patch, focusX: 50, focusY: 50, zoom: 1 };
    } else if (patch.fit === 'cover' && Object.keys(patch).length === 1 && fitMemoryRef.current[key]) {
      return { ...fitMemoryRef.current[key], ...patch };
    }
    return patch;
  };

  const handleCoverFramingChange = (face, patch, options = {}) => {
    const champ = face === 'front' ? 'frontPhotoAdjust' : 'backPhotoAdjust';
    patch = rememberFit(`cover:${face}`, coverFramingOf(face), patch);
    const next = normalizeFraming({ ...coverFramingOf(face), ...patch });
    const meta = {
      label: t('store.toasts.framingChanged'),
      coalesceKey: options.gesture ? `cover-framing:${face}` : null
    };
    if (isDefaultFraming(next)) editCover({ remove: [champ] }, meta);
    else editCover({ overrides: { [champ]: adjustmentFromFraming(next) } }, meta);
  };

  // Cadrage d'UN emplacement de la page courante (par son identifiant stable).
  // Un geste continu (glisser, molette) = un seul pas d'historique.
  const handleFramingChange = (slotIndex, patch, options = {}) => {
    const slotId = draftSlotIds[slotIndex];
    if (!slotId) return;
    patch = rememberFit(`slot:${slotId}`, normalizeFraming(draftFraming[slotId]), patch);
    editPage((model) => {
      const current = normalizeFraming(model.framing[slotId]);
      const next = normalizeFraming({ ...current, ...patch });
      const framing = { ...model.framing };
      if (isDefaultFraming(next)) delete framing[slotId];
      else framing[slotId] = next;
      return { framing };
    }, {
      label: t('store.toasts.framingChanged'),
      coalesceKey: options.gesture ? `frame:${slotId}` : null,
      fast: true
    });
  };

  // --- PANNEAU "PHOTO SELECTIONNEE" (phase 4b) ----------------------------
  const changeSelectionFraming = (patch, options) => {
    if (selectedCoverFace) handleCoverFramingChange(selectedCoverFace, patch, options);
    else if (selectedSlotIndex != null) handleFramingChange(selectedSlotIndex, patch, options);
  };
  const handlePanelZoom = (zoom) => {
    // Le curseur agit EN DIRECT sur le vrai rendu (comme la molette), puis
    // l'enregistre ; tous les crans d'un meme glissement forment un seul pas.
    if (selectionItem) {
      applyLiveToPhoto({
        target: selectedCoverFace ? { face: selectedCoverFace } : { pageIndex: currentPageIndex, spread: draftLayoutSlug === 'FULL_PHOTO_SPREAD' },
        urls: [selectionItem.url, selectionItem.metadata && selectionItem.metadata.previewUrl],
        occurrence: selectionOccurrence,
        framing: { ...selectionFraming, zoom }
      });
    }
    changeSelectionFraming({ zoom }, { gesture: true });
  };
  const handleRemoveSelection = () => {
    if (selectedCoverFace) { handleAssignCoverPhoto(selectedCoverFace, null); }
    else if (selectedSlotIndex != null) { handleRemoveSlot(selectedSlotIndex); }
    setSelectedFraming(null);
  };
  const handleReplaceSelection = () => {
    if (selectedCoverFace) handleOpenCoverPhotoPicker(selectedCoverFace);
    else if (selectedSlotIndex != null) handleOpenPhotoPicker(selectedSlotIndex);
    setSelectedFraming(null);
  };

  // Legende d'UNE photo. Une chaine vide retire la legende (et non une
  // legende vide) : c'est ainsi qu'on l'efface, sans bouton supplementaire.
  const handleSaveCaption = (itemId, texte, couleur) => {
    const valeur = (texte || '').trim();
    const teinte = couleur === 'noir' ? 'noir' : 'blanc';
    editPage((model) => {
      const previous = model.photoCaptions;
      if (!valeur) {
        if (!previous[itemId]) return {};
        const next = { ...previous };
        delete next[itemId];
        return { photoCaptions: next };
      }
      const actuelle = previous[itemId];
      const inchangee = actuelle
        && typeof actuelle === 'object'
        && actuelle.texte === valeur
        && actuelle.couleur === teinte;
      if (inchangee) return {};
      return { photoCaptions: { ...previous, [itemId]: { texte: valeur, couleur: teinte } } };
    }, { label: t('store.toasts.captionEdited'), coalesceKey: `caption:${itemId}`, fast: false });
  };

  const handleClearPage = () => {
    if (currentPageIndex == null) return;
    // Vider une moitie de double page vide aussi l'autre (meme action du
    // reducer) : une demi-photo seule n'a aucun sens.
    editPage({
      ...createEmptyModel(null),
      touched: true
    }, { label: t('store.toasts.pageCleared'), toast: true });
  };

  // Revenir au livre d'avant la generation. Remplace TOUTES les pages, y
  // compris verrouillees : l'instantane est la verite d'avant, un retour
  // partiel laisserait un livre hybride que personne n'a jamais vu.
  const handleRestoreSnapshot = async () => {
    if (!bookId || restoringSnapshot) return;
    setRestoringSnapshot(true);
    try {
      const flushed = await engine.flush();
      if (!flushed.ok) { pushToast({ id: 'offline', message: t('store.toasts.offlineBlocked') }); return; }
      const { book: updatedBook, pages: restored } = await restoreBookSnapshot(bookId);
      setBook((previous) => ({ ...previous, ...updatedBook }));
      reloadPages(restored, updatedBook);
      setSnapshot(null);
      // Tout l'affichage derive des pages : les apercus en cache decrivent
      // maintenant un livre qui n'existe plus.
      previewLoader.invalidate('all');
    } catch (_err) {
      pushToast({ id: 'restore-error', message: t('main.errors.restoreFailed') });
    } finally {
      setRestoringSnapshot(false);
    }
  };

  const handleDiscardSnapshot = async () => {
    if (!bookId) return;
    setSnapshot(null); // la proposition disparait tout de suite, c'est un geste decide
    try {
      await discardBookSnapshot(bookId);
    } catch (_err) {
      // Non bloquant : au pire le point sera ecrase a la prochaine generation.
    }
  };

  // Recharge le modele des pages apres une operation de STRUCTURE faite cote
  // serveur (deplacer, ajouter/retirer des pages, composer, restaurer) :
  // numeros de page et contenu ont change, l'historique d'avant ne designe
  // plus les bonnes pages. `extra` : { step } pour un pas unique (composition).
  const reloadPages = (freshPages, updatedBook, extra = {}) => {
    const pageCount = updatedBook?.page_count ?? book?.page_count ?? 0;
    const coverPatch = updatedBook && updatedBook.cover_overrides !== undefined
      ? { cover: { title: updatedBook.title ?? '', overrides: updatedBook.cover_overrides || {} } }
      : {};
    loadModels(freshPages, layouts, items, pageCount, { ...coverPatch, ...extra });
  };

  // Ecrit d'abord ce qui est en attente. Faux (et message) si le serveur est
  // injoignable : jamais enchainer une operation de structure sur des pages
  // pas encore enregistrees.
  const ensureSaved = async () => {
    const result = await engine.flush();
    if (!result.ok) pushToast({ id: 'offline', message: t('store.toasts.offlineBlocked') });
    return result.ok;
  };

  // Assignation directe de la photo de couverture/4e en cliquant sur
  // l'image (retour utilisateur, 2026-09-10 : "idealement modifier
  // directement sur les pages").
  const handleAssignCoverPhoto = (face, itemId) => {
    const field = face === 'front' ? 'frontPhotoId' : 'backPhotoId';
    editCover({
      overrides: { [field]: itemId },
      // Une autre photo : cadrage par defaut.
      remove: [face === 'front' ? 'frontPhotoAdjust' : 'backPhotoAdjust']
    }, {
      label: face === 'front' ? t('store.toasts.coverUpdated') : t('store.toasts.backCoverUpdated'),
      toast: true
    });
    setSelectedFraming(null);
    setSelectedSidebarItem(null);
  };

  // Envoi d'un lot de photos, une par une (jamais en parallele : le serveur
  // decode chaque image, et une rafale saturerait sa memoire).
  //
  // Deux exigences apprises d'un envoi reel de 40 photos (2026-09-12) :
  //   - AVANCEMENT visible : sans lui, plusieurs minutes sans aucun signe de
  //     vie, impossible de distinguer "ca travaille" de "c'est plante".
  //   - RESILIENCE : une photo qui echoue ne doit plus interrompre le lot.
  //     Avant, la premiere erreur faisait perdre TOUTES les photos suivantes
  //     — sur 40 photos et une coupure a la 9e, 31 etaient abandonnees sans
  //     que rien ne le dise.
  const envoyerLesPhotos = async (list) => {
    if (!book?.id || !Array.isArray(list) || list.length === 0) return;

    setUploadingPhotos(true);
    setSidebarAddError('');
    setUploadProgress({ done: 0, total: list.length, failed: 0 });

    const failures = [];
    for (let index = 0; index < list.length; index += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const created = await uploadPhoto(book.id, list[index], items.length + index);
        setItems((previous) => [...previous, created]);
      } catch (err) {
        failures.push({
          name: list[index]?.name || t('main.upload.photoFallbackName', { number: index + 1 }),
          message: friendlyMessage(err, ''),
          tooSmall: Boolean(err && err.code === 'photo_too_small')
        });
      }
      setUploadProgress({ done: index + 1, total: list.length, failed: failures.length });
    }

    if (failures.length > 0) {
      // Photos refusees parce qu'ELLES SONT TROP PETITES : un message a part, qui dit quoi faire (voir storageService.js).
      const petites = failures.filter((f) => f.tooSmall);
      const autres = failures.filter((f) => !f.tooSmall);
      const resume = (liste) => ({ names: liste.slice(0, 3).map((f) => f.name).join(', '), ellipsis: liste.length > 3 ? '…' : '' });
      const messages = [];
      if (petites.length > 0) messages.push(t('main.upload.tooSmall', { count: petites.length, ...resume(petites) }));
      if (autres.length > 0) messages.push(t('main.upload.someFailed', { count: autres.length, total: list.length, ...resume(autres) }));
      const reussies = list.length - failures.length;
      setSidebarAddError(messages.join(' ') + (reussies > 0 ? ' ' + t('main.upload.othersOk') : ''));
    }

    setUploadingPhotos(false);
    setUploadProgress(null);
  };

  // DOUBLONS : PREVENIR AVANT D'ENVOYER (2026-09-19).
  //
  // Reenvoyer le meme dossier, ou ajouter « les dernieres » en reprenant une
  // selection deja faite, remplissait la bibliotheque de photos en double —
  // qu'il fallait ensuite retrouver et supprimer une par une.
  //
  // Reconnaissance sur NOM + POIDS exact (metadata.originalName/size, pose a
  // l'upload par routes/composition.js) : deux fichiers qui partagent les
  // deux sont, en pratique, le meme fichier. On ne compare pas le contenu —
  // ce serait lire des dizaines de megaoctets dans le navigateur pour gagner
  // un cas rare — et surtout ON NE REFUSE RIEN : la meme photo peut
  // legitimement etre ajoutee deux fois. C'est un avertissement, la decision
  // reste a l'utilisateur (voir AtelierDuplicatePhotosModal).
  //
  // Les photos envoyees avant le 2026-09-19 n'ont ni nom ni poids memorises :
  // elles ne peuvent pas etre reconnues, et une signature incomplete est donc
  // ignoree plutot que de produire de faux doublons.
  const signatureFichier = (nom, poids) => {
    const nomPropre = String(nom || '').trim().toLowerCase();
    if (!nomPropre || !Number.isFinite(poids)) return null;
    return `${nomPropre}|${poids}`;
  };

  const handleUploadPhotos = async (files) => {
    const selection = Array.from(files || []);
    if (!book?.id || selection.length === 0) return;

    const dejaPresentes = new Set(
      photos
        .map((photo) => signatureFichier(photo.metadata?.originalName, Number(photo.metadata?.size)))
        .filter(Boolean)
    );

    const vues = new Set();
    const doublons = [];
    const nouvelles = [];
    selection.forEach((fichier) => {
      const cle = signatureFichier(fichier?.name, fichier?.size);
      // Deux fois le meme fichier DANS la selection compte aussi : c'est le
      // cas le plus frequent quand on ajoute un dossier par-dessus un autre.
      if (cle && (dejaPresentes.has(cle) || vues.has(cle))) {
        doublons.push(fichier);
        return;
      }
      if (cle) vues.add(cle);
      nouvelles.push(fichier);
    });

    if (doublons.length > 0) {
      setSidebarAddError('');
      setDoublonsEnAttente({ doublons, nouvelles, selection });
      return;
    }

    await envoyerLesPhotos(selection);
  };

  // ECRIRE UN SOUVENIR DEPUIS LA BIBLIOTHEQUE N EXISTE PLUS (2026-09-22).
  //
  // On ecrit desormais directement dans l'emplacement, sur la page (voir
  // AtelierTextEditor) : un texte ecrit sans savoir ou il ira produisait
  // des souvenirs orphelins, et une colonne encombree de bouts de phrases
  // qui ne trouvaient jamais leur place.
  //
  // La route d'ajout de texte, elle, reste utilisee par l'ecriture sur page.

  // "Tout supprimer" (photos ou souvenirs). Irreversible, et ca touche aussi
  // les pages deja composees : la confirmation nomme donc precisement ce qui
  // va disparaitre, jamais un "etes-vous sur ?" abstrait.
  const handleDeleteAll = async (kind) => {
    if (!book?.id || deletingAll) return;
    const concernes = kind === 'photo' ? photos : souvenirs;
    if (concernes.length === 0) return;

    const label = kind === 'photo'
      ? t('main.deleteAll.labelPhotos', { count: concernes.length })
      : t('main.deleteAll.labelMemories', { count: concernes.length });
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `${t('main.deleteAll.confirmTitle', { label })}\n\n${t('main.deleteAll.confirmBody')}`
    )) return;

    setDeletingAll(true);
    setSidebarAddError('');
    try {
      await deleteAllContentItems(book.id, kind);
      // On relit tout plutot que de retirer les elements a la main : les
      // PAGES ont change elles aussi (references nettoyees cote serveur), et
      // les deviner ici dupliquerait cette logique.
      await loadAll();
      setSelectedSidebarItem(null);
    } catch (err) {
      setSidebarAddError(t('main.errors.deleteFailed'));
    } finally {
      setDeletingAll(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!book?.id) return;
    setSidebarAddError('');
    try {
      await deleteContentItem(book.id, itemId);
      setItems((previous) => previous.filter((item) => item.id !== itemId));
      if (selectedSidebarItem?.id === itemId) setSelectedSidebarItem(null);
    } catch (err) {
      setSidebarAddError(t('main.errors.deleteFailed'));
    }
  };

  // Ouvre la modale de generation en verifiant d'abord si le contenu reel
  // suffit a atteindre le palier minimum (30 pages, PAGE_COUNT_TIERS[0] cote
  // backend voir layoutEngine.js — 2026-09-11 : porte de 28 a 30, etait 16
  // avant integration imprimeur) SANS duplication — le moteur ne repete
  // jamais une photo/un texte pour "boucher les trous" (voir
  // layoutEngine.compose, garanti par des tests dedies), donc un contenu
  // trop maigre pour 30 pages doit etre signale plutot que de generer un
  // livre presente comme fini alors qu'il ne l'est pas.
  // Plancher de pages DU FORMAT du livre (30 pour les formats historiques, celui du fournisseur sinon).
  const MIN_AUTO_PAGES = pageLimits(book?.print_format).minPages;
  const openGenerateModal = async () => {
    if (!book?.id) return;
    setGenerateError('');
    setIsGenerateModalOpen(true);
    setLoadingEstimate(true);
    try {
      const recommendation = await getRecommendedPageCount(book.id);
      // filledPages : combien de pages seront REELLEMENT remplies dans CE
      // livre. `estimatedPages` repond a une autre question (« sur combien de
      // pages ce contenu tient-il naturellement ? ») et les deux divergent
      // depuis que le moteur repartit le contenu sur le livre entier : 47
      // photos tiennent en 21 pages mais en remplissent 30.
      const pagesPrevues = recommendation?.filledPages ?? recommendation?.estimatedPages ?? null;
      setEstimatedPages(pagesPrevues);

      // Le contenu demande-t-il plus de pages que le livre n'en compte ?
      // Si oui, on va chercher les deux prix pour pouvoir annoncer l'ecart
      // en euros — « 16 pages de plus » ne dit rien, « 74,50 EUR au lieu de
      // 49 EUR » se comprend tout de suite.
      const pagesActuelles = Number(book.page_count) || 0;
      if (pagesPrevues && pagesActuelles && pagesPrevues > pagesActuelles) {
        const [actuel, prevu] = await Promise.all([
          estimatePrice(book.id, { printFormat: book.print_format, pageCount: pagesActuelles, type: 'print', quantity: 1 }).catch(() => null),
          estimatePrice(book.id, { printFormat: book.print_format, pageCount: pagesPrevues, type: 'print', quantity: 1 }).catch(() => null)
        ]);
        setDebordement({
          pagesPrevues,
          pagesActuelles,
          prixActuelCents: actuel?.unitCents ?? null,
          prixPrevuCents: prevu?.unitCents ?? null,
          currency: actuel?.currency || prevu?.currency
        });
      } else {
        setDebordement(null);
      }
    } catch (_err) {
      // Non bloquant : en cas d'echec de l'estimation, on ne bloque pas la
      // generation (mieux vaut laisser essayer que bloquer sans raison sure).
      setEstimatedPages(null);
      setDebordement(null);
    } finally {
      setLoadingEstimate(false);
    }
  };

  // "Passer en mode automatique" (plutot que "Generer automatiquement",
  // moins ambigu — ca ne donne pas l'impression d'ecraser ce qui vient
  // d'etre fait a la main) : si au moins une page a deja ete construite/
  // verrouillee manuellement, une confirmation intermediaire s'affiche
  // avant d'ouvrir le choix d'ambiance — jamais pour un livre encore vierge.
  const manualPagesCount = Object.values(bookState.pages).filter(isManualPage).length;
  const hasManualWork = manualPagesCount > 0;
  const handleGenerateButtonClick = () => {
    if (hasManualWork) {
      setIsConfirmSwitchOpen(true);
    } else {
      openGenerateModal();
    }
  };
  const handleConfirmSwitch = () => {
    setIsConfirmSwitchOpen(false);
    openGenerateModal();
  };

  // Partage entre le raccourci d'en-tete et la carte "Album collectif" plus
  // bas (retour utilisateur, 2026-09-29 : les deux declenchent la meme
  // action, un seul endroit pour la decrire).
  // « Placer sur une page » d'un mot recu (refonte du collectif, phase 3) : la PREMIERE page entierement vide recoit la mise en
  // page « Un temoignage » avec ce mot, et l'atelier s'y rend. Jamais une page qui contient deja quelque chose.
  const handlePlaceTextItem = (item) => {
    if (!item || !findAtelierLayout('ONE_TESTIMONY')) return;
    const total = book?.page_count || 0;
    let cible = null;
    for (let index = 0; index < total; index += 1) {
      const model = bookState.pages[index];
      if (model && !model.kind && !model.foreign && itemIdsOfModel(model).size === 0 && !String(model.kindFields?.text || '').trim()) {
        cible = index;
        break;
      }
    }
    if (cible === null) {
      pushToast({ id: 'place-word', message: t('store.toasts.noEmptyPageForWord') });
      return;
    }
    bookDispatch({
      type: BOOK_ACTIONS.EDIT_PAGES,
      updater: (index) => (index === cible
        ? { layoutSlug: 'ONE_TESTIMONY', slotItemIds: [item.id], slotIds: newSlotIds(1), framing: {}, photoCaptions: {}, textRoles: {}, textStyles: {}, kind: null }
        : null),
      meta: { label: t('store.toasts.wordPlaced', { page: cible + 1 }), toast: true }
    });
    goToFilmstripTarget(cible);
  };

  // LES ENVOIS DES PROCHES ARRIVENT SANS RECHARGER (phase 3) : relecture discrete de la bibliotheque toutes les 45 s, seulement
  // pour un collectif actif et quand l'onglet est visible (aucune requete pour un livre solo ou un onglet en arriere-plan).
  useEffect(() => {
    if (!book?.collective_activated_at) return undefined;
    const minuteur = setInterval(() => {
      if (document.visibilityState === 'visible') resyncItems();
    }, 45000);
    return () => clearInterval(minuteur);
  }, [book?.collective_activated_at, resyncItems]);

  const handleInvite = () => {
    setPanneauCollectif(book?.collective_activated_at ? 'manage' : 'invite');
  };

  // Jauge « 2/4 » du bouton Inviter : relue au chargement et a chaque fermeture du panneau (un invite a pu contribuer entre-temps).
  const collectifActif = Boolean(book?.collective_activated_at);
  const chargerJaugeCollectif = useCallback(async () => {
    if (!collectifActif || !bookId) { setJaugeCollectif(null); return; }
    try {
      const data = await fetchCollective(bookId);
      const participants = data?.participants || [];
      const ontContribue = participants.filter((p) => p.status === 'completed' || ((p.counts?.photos || 0) + (p.counts?.souvenirs || 0)) > 0).length;
      setJaugeCollectif({ ontContribue, total: participants.length });
    } catch (_e) { /* jauge facultative : le bouton reste utilisable sans elle */ }
  }, [collectifActif, bookId]);
  useEffect(() => { if (panneauCollectif === null) chargerJaugeCollectif(); }, [panneauCollectif, chargerJaugeCollectif]);

  // Ancienne page /book/:id/collectif (et cartes du tableau de bord) : ouvrent l'atelier avec ?collectif=1.
  const ouvrirCollectifDemande = searchParams.get('collectif') === '1';
  const collectifDejaOuvert = useRef(false);
  useEffect(() => {
    if (!ouvrirCollectifDemande || collectifDejaOuvert.current || !book) return;
    collectifDejaOuvert.current = true;
    if (book.collection_mode !== 'solo' && !(book.locked_at && !book.bookEditBypassed)) {
      setPanneauCollectif(book.collective_activated_at ? 'manage' : 'invite');
    }
  }, [ouvrirCollectifDemande, book]);

  // "Dupliquer pour modifier" (artefact "Atelier Bookipix", 2026-10-11) — un
  // livre verrouille (commande deja payee) ne peut plus etre modifie ici ;
  // cette copie, non verrouillee, repart de ce qui existait. Navigue
  // directement vers l'atelier de la copie des qu'elle existe, comme pour
  // n'importe quelle creation de livre.
  const handleDuplicateBook = async () => {
    if (duplicating) return;
    setDuplicating(true);
    setDuplicateError('');
    try {
      const copie = await duplicateBook(book.id);
      navigate(`/book/${copie.id}/atelier`);
    } catch (err) {
      setDuplicateError(t('main.lockedBanner.duplicateError'));
      setDuplicating(false);
    }
  };

  // Generation automatique (bouton "Generer automatiquement", voir
  // AtelierGenerateModal) : meme route/mecanisme que l'ancien assistant
  // /composer (compose puis replaceBookPages, respecte deja `locked`), mais
  // declenchee depuis l'atelier avec une ambiance choisie. Apres succes,
  // l'utilisateur feuillette directement le resultat dans l'atelier — pas de
  // second ecran de "preview avant de garder".
  // Deplacement d'une page (glisser-deposer dans le filmstrip). Aucun contenu
  // n'est reecrit : seuls les numeros de page changent cote serveur.
  //
  // Apres coup, TOUT le cache d'apercus est jete : entre la page de depart et
  // la page d'arrivee, chaque numero designe desormais une autre page, et une
  // vignette conservee afficherait le contenu du voisin. `contentVersion`
  // reinitialise le brouillon de la page selectionnee pour la meme raison.
  //
  // On SUIT la page deplacee (setViewIndex/selectedSide sur sa nouvelle
  // position) : la relacher et ne plus savoir ou elle est atterri serait la
  // pire facon de finir le geste.
  // Meme correspondance que bookContentService.movePage cote serveur (miroir
  // assume, comme ailleurs dans ce projet) : ou atterrit la page `index`
  // apres un deplacement fromIndex -> toIndex ? Extraite en fonction pure
  // (plutot qu'une fermeture locale a handleMovePage) pour pouvoir aussi
  // predire, SANS second aller-retour reseau, ou atterrit la JUMELLE d'une
  // double page apres avoir deplace sa moitie (voir handleMoveSpreadPages).
  const nextIndexAfterMove = (index, fromIndex, toIndex) => {
    if (index === fromIndex) return toIndex;
    if (fromIndex < toIndex) return index > fromIndex && index <= toIndex ? index - 1 : index;
    return index >= toIndex && index < fromIndex ? index + 1 : index;
  };

  // Deplacement BAS NIVEAU d'UNE SEULE page cote serveur + mise a jour du
  // cache local. Ne touche PAS viewIndex/selectedSide : l'appelant (simple
  // ou double page) decide une seule fois, a la toute fin, quelle page
  // suivre — sinon un deplacement en deux temps (voir plus bas) ferait
  // suivre la jumelle au lieu de la page que l'utilisateur a reellement
  // saisie.
  const moveSinglePageOnly = async (fromIndex, toIndex) => {
    if (!book?.id || fromIndex === toIndex) return;
    const result = await movePage(book.id, fromIndex, toIndex);

    // L'apercu d'une page deplacee n'a pas change : c'est la MEME page, a
    // une autre place. On permute donc les entrees deja en memoire plutot
    // que de recharger — un rechargement donnait un resultat INTERMITTENT
    // puis systematiquement faux (2026-09-13). Permuter est instantane et ne
    // peut pas se tromper : aucune requete a arbitrer.
    //
    // SEULE reserve : en format Luxe, la page porte un petit numero en
    // coin, qui restera l'ancien jusqu'au prochain chargement naturel de
    // cette page.
    previewLoader.remapPages((index) => nextIndexAfterMove(index, fromIndex, toIndex));
    // Numeros et contenu ont change : le modele des pages est recharge depuis
    // la reponse du serveur (l'historique d'avant ne designerait plus les
    // bonnes pages, il repart de zero).
    reloadPages(result?.pages || [], null);
  };

  // Retour utilisateur (2026-10-06) : "deplacer toute la photo lorsqu'il
  // s'agit du deplacement d'une photo qui occupe une double page" —
  // handleMovePage ne connaissait rien des doubles pages : deplacer UNE
  // moitie la detachait de sa jumelle (restee derriere, a son ancienne
  // place), desynchronisant le photo-spread (chaque moitie se met ensuite a
  // afficher selon sa PROPRE parite d'index — voir pageRenderer.js — donc
  // potentiellement les DEUX memes moitie, ou deux moities qui ne se font
  // plus face du tout).
  //
  // La cible `toIndex` (une position simple, 1..totalPages, choisie par le
  // popover ou le glisser-deposer) est donc interpretee comme "dans quel
  // vis-a-vis cette double page doit-elle atterrir" (spreadOfPage), jamais
  // comme une position page-a-page brute. Le vis-a-vis 0 (la toute premiere
  // page, seule a droite) ne peut structurellement pas porter de double
  // page ; de meme, un livre a pagination PAIRE se termine sur une page
  // gauche sans vis-a-vis (voir doublePagePossible) — dans les deux cas on
  // se rabat sur le dernier vis-a-vis pleinement valide.
  //
  // DEUX appels sequentiels au deplacement SIMPLE deja eprouve, jamais un
  // SEUL calcul de decalage de bloc ecrit a la main — mais l'ORDRE compte :
  // chaque moitie vise TOUJOURS sa propre place finale (jamais celle de
  // l'autre), et c'est seulement le sens du deplacement qui decide laquelle
  // part en premier (vers l'avant : la DROITE d'abord, puis la GAUCHE ;
  // vers l'arriere : l'inverse). L'ordre inverse echange systematiquement
  // gauche et droite au lieu de les preserver — verifie par un balayage
  // exhaustif de tous les deplacements possibles sur deux tailles de livre
  // avant d'ecrire cette version.
  const handleMoveSpreadPages = async (fromIndex, toIndex) => {
    const otherIndex = facingPageIndex(fromIndex);
    if (otherIndex == null) return; // jamais vrai en pratique (voir doublePagePossible), filet de securite
    const fromIsLeft = isLeftPage(fromIndex);
    const leftIdx = fromIsLeft ? fromIndex : otherIndex;
    const rightIdx = fromIsLeft ? otherIndex : fromIndex;

    const maxValidSpread = Math.floor((totalPages - 1) / 2);
    const targetSpread = Math.max(1, Math.min(spreadOfPage(toIndex), maxValidSpread));
    const { left: targetLeft, right: targetRight } = spreadPair(targetSpread);

    if (targetLeft > leftIdx) {
      if (rightIdx !== targetRight) await moveSinglePageOnly(rightIdx, targetRight);
      const leftAfter = nextIndexAfterMove(leftIdx, rightIdx, targetRight);
      if (leftAfter !== targetLeft) await moveSinglePageOnly(leftAfter, targetLeft);
    } else if (targetLeft < leftIdx) {
      if (leftIdx !== targetLeft) await moveSinglePageOnly(leftIdx, targetLeft);
      const rightAfter = nextIndexAfterMove(rightIdx, leftIdx, targetLeft);
      if (rightAfter !== targetRight) await moveSinglePageOnly(rightAfter, targetRight);
    }
    // targetLeft === leftIdx : deja a la bonne place, rien a deplacer.

    setViewIndex(targetSpread + 1);
    setSelectedSide(fromIsLeft ? 'left' : 'right');
  };

  const handleMovePage = async (fromIndex, toIndex) => {
    if (!book?.id || fromIndex === toIndex) return;
    setMovingPage(true);
    try {
      // Les modifications en attente partent d'abord : le serveur deplace ce
      // qui est enregistre, pas ce qui est encore a l'ecran.
      if (!(await ensureSaved())) return;
      if (savedPageIsSpread(fromIndex)) {
        await handleMoveSpreadPages(fromIndex, toIndex);
      } else {
        await moveSinglePageOnly(fromIndex, toIndex);
        setViewIndex(spreadOfPage(toIndex) + 1);
        setSelectedSide(isLeftPage(toIndex) ? 'left' : 'right');
      }
      // Volontairement AUCUN rechargement d'apercu ici : la permutation locale
      // suffit, et toute requete supplementaire ne ferait que reintroduire la
      // course decrite plus haut.
    } catch (_err) {
      pushToast({ id: 'move-error', message: t('main.errors.moveFailed') });
    } finally {
      setMovingPage(false);
    }
  };

  const handleGenerate = async (mood) => {
    if (!book?.id) return;
    setIsGenerating(true);
    setGenerateError('');
    try {
      // Ce que l'utilisateur vient de faire doit etre enregistre AVANT que le
      // serveur recompose : il respecte les pages verrouillees qu'il connait.
      if (!(await ensureSaved())) { setIsGenerating(false); return; }
      await composeBook(book.id, generateVariant, mood);
      setGenerateVariant((previous) => previous + 1);
      const freshPages = await listPages(book.id);
      // Le serveur peut avoir REDIMENSIONNE le livre (le contenu ne tient pas, ou
      // tient en moins de pages) : on relit la ligne du livre pour connaitre son
      // vrai nombre de pages (et le prix qui va avec).
      const { data: refreshedBook } = await fetchBookRow(book.id);
      if (refreshedBook) setBook(refreshedBook);
      // Un SEUL pas d'historique : "annuler" revient d'un coup au livre
      // d'avant. (Avant, il fallait reconstruire le brouillon a la main pour ne
      // pas effacer la page affichee — constate le 2026-09-21 : "il laisse
      // toujours la premiere page sans rien" ; avec un modele par page, cette
      // course n'existe plus.)
      reloadPages(freshPages, refreshedBook || null, { step: { label: t('store.toasts.composed') }, keepHistory: true });
      previewLoader.invalidate('all');
      // Le serveur vient de poser un point de restauration (ou n'a pas pu :
      // voir routes/composition.js POST /compose). On le relit plutot que de
      // le deduire, pour ne proposer un retour en arriere que s'il existe
      // reellement.
      await refreshSnapshot();
      setIsGenerateModalOpen(false);
      setViewIndex(1);
      setSelectedSide('left');
    } catch (err) {
      setGenerateError(friendlyMessage(err, t('main.errors.generateFailed')));
    } finally {
      setIsGenerating(false);
    }
  };

  // Prix apres un ajout/retrait de pages (§7/§8) : reprend le prix DEJA
  // affiche (priceInfo, avant l'action) pour calculer l'ecart, puis va
  // chercher le nouveau prix — jamais la formule recalculee ici, toujours
  // estimatePrice. `newPageCount` passe explicitement (pas book.page_count,
  // pas encore a jour au moment ou ceci est appele) : plus sur qu'attendre
  // le prochain rendu.
  const announcePriceChange = async (newPageCount, pageDelta) => {
    if (!book?.id || !book?.print_format) return;
    const before = priceInfo?.bookPriceCents ?? null;
    try {
      const result = await estimatePrice(book.id, {
        printFormat: book.print_format,
        pageCount: newPageCount,
        type: 'print',
        quantity: 1
      });
      setPriceInfo(result);
      if (before != null && Number.isFinite(result?.bookPriceCents)) {
        const diffCents = result.bookPriceCents - before;
        if (diffCents !== 0) showPriceDelta({ pages: pageDelta, priceCents: diffCents, currency: result.currency });
      }
    } catch (_err) {
      // Non bloquant : un echec laisse simplement l'ancien prix affiche.
    }
  };

  // Agrandir volontairement le livre (bouton "+2" du filmstrip) — jamais une
  // recomposition, jamais touche aux pages existantes (voir
  // routes/composition.js: POST /pages/extend). Meme mecanique de
  // rafraichissement que handleGenerate ci-dessus (pages + book.page_count +
  // refreshToken).
  const handleAddPages = async () => {
    if (!book?.id || addingPages) return;
    setAddingPages(true);
    try {
      if (!(await ensureSaved())) return;
      const oldCount = Number(book.page_count) || 0;
      const { book: updatedBook, pages: freshPages } = await extendBookPages(book.id, 2);
      setBook((previous) => ({ ...previous, ...updatedBook }));
      reloadPages(freshPages, updatedBook);
      announcePriceChange(updatedBook.page_count, 2);
      // On se place sur la premiere des deux pages ajoutees (apres la reconstruction du livre).
      setPendingPageTarget(oldCount);
    } catch (_err) {
      pushToast({ id: 'pages-error', message: t('main.errors.addPagesFailed') });
    } finally {
      setAddingPages(false);
    }
  };

  // Inserer 2 pages vides ENTRE deux doubles pages (bouton "+" de la pellicule).
  // Le serveur sait ajouter des pages A LA FIN (extend) et deplacer une page
  // (move) : on ajoute les 2 pages, puis on les amene a la bonne place. Les deux
  // sont vides, leur parite n'a aucune consequence ; toutes les pages suivantes
  // gardent leur cote (decalage de 2).
  const handleInsertPages = async (pageIndex) => {
    if (!book?.id || addingPages) return;
    setAddingPages(true);
    try {
      if (!(await ensureSaved())) return;
      const oldCount = Number(book.page_count) || 0;
      const { book: updatedBook } = await extendBookPages(book.id, 2);
      await movePage(book.id, oldCount, pageIndex);
      const moved = await movePage(book.id, oldCount + 1, pageIndex + 1);
      setBook((previous) => ({ ...previous, ...updatedBook }));
      // Les apercus suivent leurs pages (decalage de 2), sans aucun appel reseau.
      previewLoader.remapPages((index) => (index >= pageIndex ? index + 2 : index));
      reloadPages(moved?.pages || [], updatedBook);
      announcePriceChange(updatedBook.page_count, 2);
      // On se place sur la premiere des deux nouvelles pages (apres la reconstruction du livre, qui repart de la
      // couverture : voir l'effet pendingPageTarget).
      setViewIndex(spreadOfPage(pageIndex) + 1);
      setSelectedSide('left');
      setPendingPageTarget(pageIndex);
    } catch (_err) {
      pushToast({ id: 'pages-error', message: t('main.errors.addPagesFailed') });
      // L'etat serveur a peut-etre change a mi-chemin : on relit tout.
      loadAll();
    } finally {
      setAddingPages(false);
    }
  };

  // Supprimer 2 pages ENTRE d'autres (bouton "x" de la pellicule) : le serveur
  // sait retirer des pages A LA FIN (shrink) et en deplacer (move). On amene
  // donc la paire a supprimer tout au bout, puis on retire les 2 dernieres.
  // `leftIndex` : la page de GAUCHE de la double page visee (index impair).
  const handleDeletePages = async (leftIndex) => {
    if (!book?.id || removingPages) return;
    const total = Number(book.page_count) || 0;
    if (total - 2 < MIN_AUTO_PAGES) {
      pushToast({ id: 'pages-error', message: t('pageFilmstrip.removeTitleDisabled', { min: MIN_AUTO_PAGES }) });
      return;
    }
    // Du contenu sur ces pages : on demande avant (jamais une suppression
    // silencieuse). Pages vides : directement.
    const hasContent = [leftIndex, leftIndex + 1].some((index) => isManualPage(bookState.pages[index]));
    if (hasContent) {
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm(t('main.removePages.confirmContent', { numbers: `${leftIndex + 1}${t('main.removePages.and')}${leftIndex + 2}` }))) return;
    }
    setRemovingPages(true);
    try {
      if (!(await ensureSaved())) return;
      // Les deux pages, l'une apres l'autre, tout au bout (la seconde, une fois
      // la premiere partie, a pris sa place a `leftIndex`).
      await movePage(book.id, leftIndex, total - 1);
      await movePage(book.id, leftIndex, total - 1);
      const { book: updatedBook, pages: freshPages } = await shrinkBookPages(book.id, 2, true);
      setBook((previous) => ({ ...previous, ...updatedBook }));
      previewLoader.remapPages((index) => {
        if (index === leftIndex || index === leftIndex + 1) return null;
        return index > leftIndex + 1 ? index - 2 : index;
      });
      reloadPages(freshPages, updatedBook);
      announcePriceChange(updatedBook.page_count, -2);
      const landing = Math.min(leftIndex, (updatedBook.page_count || total - 2) - 1);
      setViewIndex(spreadOfPage(landing) + 1);
      setSelectedSide(isLeftPage(landing) ? 'left' : 'right');
    } catch (_err) {
      pushToast({ id: 'pages-error', message: t('main.errors.removePagesFailed') });
      loadAll();
    } finally {
      setRemovingPages(false);
    }
  };

  // Reduire volontairement le livre (bouton "-2" du filmstrip) — contrepartie
  // exacte de handleAddPages, meme mecanique de rafraichissement.
  //
  // Le serveur retire par la FIN et refuse net de descendre sous le minimum
  // imprimable. Si les dernieres pages ne sont PAS vides, il repond 409
  // needsConfirmation avec les numeros concernes plutot que de supprimer :
  // on pose alors la question, en nommant les pages. Ne jamais transformer ce
  // 409 en simple message d'erreur — l'utilisateur croirait l'action
  // impossible alors qu'elle attend juste son accord.
  const handleRemovePages = async () => {
    if (!book?.id || removingPages) return;

    const applyResult = ({ book: updatedBook, pages: freshPages }) => {
      setBook((previous) => ({ ...previous, ...updatedBook }));
      reloadPages(freshPages, updatedBook);
      announcePriceChange(updatedBook.page_count, -2);
    };

    setRemovingPages(true);
    try {
      if (!(await ensureSaved())) { setRemovingPages(false); return; }
      applyResult(await shrinkBookPages(book.id, 2));
    } catch (err) {
      const details = err?.payload;
      if (details?.needsConfirmation) {
        const numbers = (details.pageNumbers || []).join(t('main.removePages.and'));
        const message = details.lockedCount > 0
          ? t('main.removePages.confirmLocked', { numbers })
          : t('main.removePages.confirmContent', { numbers });
        // eslint-disable-next-line no-restricted-globals
        if (!window.confirm(message)) {
          setRemovingPages(false);
          return;
        }
        try {
          applyResult(await shrinkBookPages(book.id, 2, true));
        } catch (_confirmErr) {
          pushToast({ id: 'pages-error', message: t('main.errors.removePagesFailed') });
        }
      } else {
        pushToast({ id: 'pages-error', message: t('main.errors.removePagesFailed') });
      }
    } finally {
      setRemovingPages(false);
    }
  };

  const canGoPrevious = viewIndex > 0;
  const canGoNext = viewIndex < lastViewIndex;

  // RACCOURCIS CLAVIER (phase 4b) : fleches gauche/droite = page precedente /
  // suivante, Suppr = retirer la photo selectionnee (le message "Annuler" suit),
  // Ctrl+Z = annuler (voir store/BookStoreProvider). Jamais pendant la saisie
  // d'un texte, ni quand une fenetre ou le selecteur de photos est ouvert.
  const keyboardRef = useRef({});
  keyboardRef.current = {
    viewIndex, lastViewIndex, selectedSlotIndex, selectedCoverFace, handleRemoveSelection, focusMode, panelDrawerOpen,
    pickerOpen: pickerTargetSlotIndex != null || pickerTargetCoverFace != null,
    closePicker: handleClosePhotoPicker,
    blocked: pickerTargetSlotIndex != null || pickerTargetCoverFace != null || editingTextSlot != null
      || isFinishModalOpen || isGenerateModalOpen || isConfirmSwitchOpen || showOnboarding
  };
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      if (target && target.closest && target.closest('[role=dialog], .atelier-modal')) return;
      const state = keyboardRef.current;
      // Echap ferme TOUJOURS le choix de photo : sur un ecran etroit ou en mode Agrandir il monte en feuille qui recouvre le
      // livre, et sans ce raccourci (bloque par `blocked` ci-dessous) il ne restait que le bouton « Annuler » (retour 2026-10-21).
      if (event.key === 'Escape' && state.pickerOpen) { event.preventDefault(); state.closePicker(); return; }
      if (state.blocked) return;
      if (event.key === 'f' || event.key === 'F') { event.preventDefault(); setFocusMode((value) => !value); return; }
      if (event.key === 'Escape') {
        if (state.focusMode) { event.preventDefault(); setFocusMode(false); }
        else if (state.panelDrawerOpen) { event.preventDefault(); setPanelDrawerOpen(false); }
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const next = state.viewIndex + (event.key === 'ArrowRight' ? 1 : -1);
        if (next < 0 || next > state.lastViewIndex) return;
        event.preventDefault();
        goToViewRef.current(next);
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        if (state.selectedSlotIndex == null && !state.selectedCoverFace) return;
        event.preventDefault();
        state.handleRemoveSelection();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const goToView = (nextIndex) => {
    setViewIndex(nextIndex);
    setSelectedSide('left');
  };
  const goToViewRef = useRef(goToView);
  goToViewRef.current = goToView;

  // Saut direct depuis le filmstrip (voir AtelierPageFilmstrip) : 'cover'/
  // 'back-cover' pour les couvertures, sinon un index de PAGE reelle (pas un
  // index de double-page) — convertit vers le viewIndex/selectedSide qui la
  // contient, cote gauche ou droit selon la parite (meme logique que
  // leftPageIndex/rightPageIndex plus haut : page paire = gauche, impaire =
  // droite d'une meme double-page).
  // « Terminer mon livre » (correctif 12) : mene DIRECTEMENT a l'apercu complet. Les pages en attente sont
  // enregistrees d'abord. Seul un livre ENTIEREMENT vide (rien a previsualiser) ouvre une fenetre.
  // Livre verrouille (423) : une seule explication claire, au lieu d un « Hors ligne » qui reessaie sans fin.
  useEffect(() => {
    if (saveStatus === 'locked' && !lockedToldRef.current) {
      lockedToldRef.current = true;
      pushToast({ id: 'book-locked', message: t('store.toasts.bookLocked') });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveStatus]);

  const handleFinishBook = async () => {
    const hasContent = finishStats.pageStatuses.some((entry) => entry.status !== 'empty');
    if (!hasContent) { setIsFinishModalOpen(true); return; }
    setFinishing(true);
    try {
      const saved = await ensureSaved();
      if (saved) navigate(`/book/${bookId}/apercu`);
    } finally {
      setFinishing(false);
    }
  };

  useEffect(() => {
    if (pendingPageTarget == null) return undefined;
    const target = pendingPageTarget;
    const timer = setTimeout(() => { goToFilmstripTarget(target); setPendingPageTarget(null); }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPageTarget, totalPages]);

  const goToFilmstripTarget = (target) => {
    if (target === 'cover') {
      setViewIndex(0);
      setSelectedSide('left');
      return;
    }
    if (target === 'back-cover') {
      setViewIndex(lastViewIndex);
      setSelectedSide('left');
      return;
    }
    setViewIndex(spreadOfPage(target) + 1);
    setSelectedSide(isLeftPage(target) ? 'left' : 'right');
  };

  const draftSlotItems = draftSlotItemIds.map((id) => (id ? itemsById[id] : null));

  // Etat du panneau "photo selectionnee" (les gestionnaires sont plus haut).
  const selectedSlotItem = selectedSlotIndex != null ? draftSlotItems[selectedSlotIndex] : null;
  const selectionItem = selectedCoverFace ? coverPhotoItem : selectedSlotItem;
  const selectionFraming = selectedCoverFace
    ? coverFramingOf(selectedCoverFace)
    : normalizeFraming(selectedSlotIndex != null ? draftFraming[draftSlotIds[selectedSlotIndex]] : null);
  const selectionOccurrence = selectedSlotIndex != null && selectedSlotItem
    ? draftSlotItems.slice(0, selectedSlotIndex).filter((other) => other && other.id === selectedSlotItem.id).length : 0;
  const selectionCropped = !selectedCoverFace && selectedSlotItem
    ? slotCroppedFraction({ item: selectedSlotItem, layoutSlug: draftLayoutSlug, slotIndex: selectedSlotIndex, printFormat: book?.print_format, framing: selectionFraming })
    : 0;
  const hasContent = isManualPage(pageModel);

  // Incrustation de glisser-deposer directe sur la page (en plus du panneau
  // "Mise en page", pas a sa place) : memes emplacements/etat, voir
  // AtelierPageOverlay. Rien a afficher tant qu'aucun format n'est choisi
  // pour la page courante (repli sur le panneau de droite, comme avant).
  // Actions posees sur la page en cours de modification (coin bas droit) —
  // deplacer la page dans le livre, la vider. Elles etaient dans le panneau de
  // droite, qui melangeait ainsi le travail de mise en page et les actions sur
  // la page ; elles vivent desormais sur la page, comme l'oeil "voir a
  // l'echelle" a son coin haut droit (2026-09-13).
  const pageActions = viewKind === 'spread' && currentPageIndex != null ? (
    <AtelierPageActions
      pageNumber={currentPageIndex + 1}
      totalPages={totalPages}
      // Le composant raisonne en numeros AFFICHES (1-based), le moteur en
      // index : la conversion se fait ici, une seule fois.
      onMoveToPosition={(oneBased) => handleMovePage(currentPageIndex, oneBased - 1)}
      movingPage={movingPage}
      onClearPage={handleClearPage}
      hasContent={hasContent}
      onOpenLayoutDrawer={openLayoutPanel}
      // Menu « ⋯ » : 2 pages inserees avant la double page de la page courante, ou cette double page supprimee.
      // (La page 1, seule a droite, n'a pas de double page a supprimer ; on insere avant la page 2.)
      onInsertPagesBefore={() => handleInsertPages(currentPageIndex % 2 === 1 ? currentPageIndex : Math.max(1, currentPageIndex - 1))}
      onDeleteSpread={() => handleDeletePages(currentPageIndex % 2 === 1 ? currentPageIndex : currentPageIndex - 1)}
      canDeleteSpread={currentPageIndex >= 1 && totalPages - 2 >= MIN_AUTO_PAGES}
    />
  ) : null;

  // Point de restauration (apres une composition automatique) : message
  // flottant PERSISTANT en bas de l'ecran, jamais un bandeau qui pousse le
  // livre. Deux gestes explicites : revenir au livre d'avant, ou garder.
  // (Les anciens bandeaux "Annuler — ..." et "Vous testez la version
  // automatique" deplacaient la mise en page ; l'annulation d'un geste passe
  // maintenant par le message flottant et Ctrl+Z, voir store/BookStoreProvider.)
  const snapshotHandlersRef = useRef({});
  snapshotHandlersRef.current = {
    restore: handleRestoreSnapshot,
    discard: () => { dismissToast('snapshot'); handleDiscardSnapshot(); }
  };
  const snapshotSignature = snapshot ? `${snapshot.createdAt || ''}|${snapshot.pageCount || 0}|${snapshot.manualPages || 0}` : null;
  useEffect(() => {
    if (!snapshot) { dismissToast('snapshot'); return; }
    let detail = '';
    if (snapshot.pageCount) {
      detail = snapshot.manualPages
        ? ` (${t('main.snapshotBar.pageCount', { count: snapshot.pageCount })}, ${t('main.snapshotBar.withManual', { count: snapshot.manualPages })})`
        : ` (${t('main.snapshotBar.pageCount', { count: snapshot.pageCount })})`;
    }
    pushToast({
      id: 'snapshot',
      persistent: true,
      message: `${t('main.snapshotBar.title')} ${t('main.snapshotBar.kept')}${detail}`,
      actionLabel: `↩ ${t('main.snapshotBar.restore')}`,
      onAction: () => snapshotHandlersRef.current.restore(),
      extraActions: [{ label: t('main.snapshotBar.keep'), onClick: () => snapshotHandlersRef.current.discard() }]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotSignature]);

  const draftLayoutForOverlay = draftLayoutSlug ? findAtelierLayout(draftLayoutSlug) : null;
  // DOUBLE PAGE : la photo couvre les deux pages, mais l incrustation (le cadre pointille) n est posee que sur la page
  // selectionnee. Tant que l emplacement est vide, la page JUMELLE montre le meme cadre pointille (visuel seulement, les
  // clics passent a la page) : on voit que le modele est bien actif sur les deux pages (retour utilisateur 2026-10-17).
  const twinPageIndex = viewKind === 'spread' && draftLayoutForOverlay && draftLayoutForOverlay.spread && currentPageIndex != null
    && !(draftSlotItems[0])
    ? facingPageIndex(currentPageIndex)
    : null;
  const twinInset = getOverlayInsetPct(book?.print_format);
  const twinOverlay = twinPageIndex != null && twinPageIndex < totalPages ? (
    <div className="atelier-page-overlay" style={{ top: `${twinInset.top}%`, left: `${twinInset.left}%`, right: `${twinInset.right}%`, bottom: `${twinInset.bottom}%` }}>
      <div className={`atelier-overlay-slot is-ghost ${pickerTargetSlotIndex === 0 ? 'is-pick-target' : ''}`} style={{ top: 0, left: 0, width: '100%', height: '100%' }}>
        <span className="atelier-overlay-slot-add">
          <b aria-hidden="true">+</b>
          <span>{t('pageOverlay.slotLabels.photo')}</span>
        </span>
      </div>
    </div>
  ) : null;
  const pageOverlay = viewKind === 'spread' && draftLayoutForOverlay ? (
    <AtelierPageOverlay
      key={currentPageIndex}
      slug={draftLayoutForOverlay.slug}
      slotTypes={draftLayoutForOverlay.slots}
      slotItems={draftSlotItems}
      onAssignSlot={handleAssignSlot}
      onRemoveSlot={handleRemoveSlot}
      pageIndex={currentPageIndex}
      slotIds={draftSlotIds}
      framing={draftFraming}
      selectedSlotIndex={selectedSlotIndex}
      onSelectSlot={(slotIndex) => setSelectedFraming(slotIndex == null ? null : { type: 'slot', pageIndex: currentPageIndex, slotIndex })}
      onFramingChange={handleFramingChange}
      onSwapSlots={handleSwapSlots}
      pickerSlotIndex={pickerTargetSlotIndex}
      pickerPhotos={photosNonUtilisees}
      onPickPhoto={handlePickPhotoFromLibrary}
      onPickerUpload={handleUploadPhotoForPicker}
      pickerUploading={pickerUploading}
      pickerError={pickerUploadError}
      onClosePicker={handleClosePhotoPicker}
      editingIndex={editingTextSlot}
      onEditingChange={setEditingTextSlot}
      selectedSidebarItem={selectedSidebarItem}
      photoCaptions={draftPhotoCaptions}
      onSaveCaption={handleSaveCaption}
      printFormat={book?.print_format}
      onSaveText={handleSaveText}
      onCreateText={handleCreateText}
      textRoles={draftTextRoles}
      textStyles={draftTextStyles}
      onOpenPhotoPicker={handleOpenPhotoPicker}
      onOpenLayoutDrawer={openLayoutPanel}
      onContinueNextPage={currentPageIndex != null && currentPageIndex + 1 < totalPages
        ? (carryText) => setContinuation({ pageIndex: currentPageIndex + 1, text: carryText || '' })
        : null}
    />
  ) : (viewKind === 'spread' && draftPageKind ? (
    // Dedicace / Chapitre / Remerciements : on remplit DIRECTEMENT sur la page.
    <AtelierPageKindOverlay
      kind={draftPageKind}
      fields={draftPageKindFields}
      onFieldChange={handlePageKindFieldChange}
      photos={photos}
      onPickPhoto={() => handleOpenPhotoPicker(-1)}
    />
  ) : null);

  // Libelle du format courant, pour l'en-tete. FORMAT_DIMENSIONS_MM est la
  // meme table que celle utilisee par les miniatures et le controle qualite :
  // une seule source, pas un troisieme jeu de dimensions.
  const formatCourant = (() => {
    const id = book?.print_format;
    if (!id) return null;
    const dims = FORMAT_DIMENSIONS_MM[id];
    if (!dims) return null;
    const noms = { livret: t('main.formats.livret'), standard: t('main.formats.standard'), luxe: t('main.formats.luxe') };
    return {
      nom: noms[id] || (catalogFormatName(id) && catalogFormatName(id).nom) || id,
      taille: `${formatCm(dims.widthMm, i18n.language)} × ${formatCm(dims.heightMm, i18n.language)} cm`
    };
  })();

  // Retour utilisateur (2026-10-04, capture a l'appui) : "le chiffre de page
  // ne se met pas a jour en bas lorsque je me mets sur une page impaire" —
  // ce libelle utilisait toujours leftPageIndex, jamais la PAGE REELLEMENT
  // SELECTIONNEE (selectedSide peut valoir 'right'). currentPageIndex,
  // juste au-dessus, fait deja ce calcul correctement (avec le meme repli
  // sur le cote existant) — le reutiliser ici plutot que de dupliquer la
  // logique une deuxieme fois, pres a diverger.
  const navLabel = viewKind === 'cover'
    ? t('bookView.coverLabel')
    : viewKind === 'back-cover'
      ? t('bookView.backCoverLabel')
      : t('bookView.pageLabel', { number: currentPageIndex + 1 });

  if (loading || formatsPending) {
    return <div className="atelier-loading atelier-loading-screen"><BookipixLoader size="lg" label={t('main.loading')} /></div>;
  }

  if (!book) {
    return <div className="atelier-loading">{error || t('main.errors.bookNotFound')}</div>;
  }

  return (
    <div className="atelier-container">
      {/* Rappel discret quand on travaille sans compte : c'est l'ecran ou
          l'on passe le plus de temps, donc celui ou il faut le dire. */}
      <AnonymousBanner compact />
      {/* EN-TETE (refonte visuelle 2026-09-25, elargie 2026-09-29, Gabarit 2
          "bandeau test detachable" le meme jour — voir l'artefact "Bookipix
          — pistes d'interface", onglet "Barre d'atelier").
          Remplace ce que le site affichait par-dessus l'atelier jusqu'ici
          (logo, "Comment ca marche", tableau de bord, administration,
          email, deconnexion — voir Layout.js, qui ne rend plus ce bandeau
          sur cette route) : ← Retour, le titre, le format. Porte aussi
          desormais la barre Photos/Mise en page/Pages et le raccourci
          Inviter/Gerer le collectif — plus de chrome permanent qu'a
          l'origine de cette refonte, mais tout regroupe au meme endroit
          plutot que disperse sur plusieurs lignes/sections. Le mode
          automatique reste un lien texte, explique au survol seulement
          (§9 de la demande initiale). */}
      {/* book.locked_at seul ne suffit pas : un livre verrouille en
          PRODUCTION reelle affichait quand meme "Mode test, modifiable
          malgre le paiement" (retour utilisateur, 2026-10-05, sur une
          commande reellement payee) alors que le serveur bloquait deja
          toute modification (423). book.bookEditBypassed (routes/books.js)
          reflete le VRAI etat : locked_at ET le bypass de mode test actif. */}
      {book.bookEditBypassed && showTestBanner && (
        <div className="atelier-test-banner">
          <span aria-hidden="true">🔧</span> <b>{t('main.testBanner.title')}</b> — {t('main.testBanner.body')}
          <button
            type="button"
            className="atelier-test-banner-close"
            onClick={() => setShowTestBanner(false)}
            title={t('main.testBanner.hide')}
            aria-label={t('main.testBanner.hideAria')}
          >
            ×
          </button>
        </div>
      )}
      {/* Pendant qu'une commande reelle bloquait vraiment la sauvegarde
          (423, verifie), rien ne le signalait a l'ecran : l'utilisateur
          pouvait continuer a glisser des photos, taper du texte, sans
          jamais savoir que rien n'etait enregistre (retour utilisateur,
          2026-10-05 : "pourtant je peux modifier le livre" — vrai a
          l'ecran, faux une fois la sauvegarde tentee).
          Restyle 2026-10-11 (artefact "Atelier Bookipix" : "aujourd'hui un
          bandeau rouge annonce que les modifications ne seront pas
          enregistrees... un bandeau doux et un bouton 'Dupliquer pour
          modifier'") — bandeau adouci (plus de rouge/🔒 isole) + action
          reelle (handleDuplicateBook, POST .../duplicate) au lieu d'une
          simple information sans issue. */}
      {book.locked_at && !book.bookEditBypassed && (
        <div className="atelier-locked-banner">
          <span className="atelier-locked-banner-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18"><rect x="5" y="11" width="14" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.9" /><path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /></svg>
          </span>
          <p>
            <b>{t('main.lockedBanner.title')}</b>
            {t('main.lockedBanner.body')}
          </p>
          <div className="atelier-locked-banner-actions">
            <button type="button" className="btn btn-sm" onClick={handleDuplicateBook} disabled={duplicating}>
              {duplicating ? t('main.lockedBanner.duplicating') : t('main.lockedBanner.duplicate')}
            </button>
            {duplicateError && <span className="atelier-locked-banner-error">{duplicateError}</span>}
          </div>
        </div>
      )}

      <header className="atelier-header">
        <div className="atelier-header-top">
          {/* Plus de lien vers /book/:bookId (l'onglet "Edition" n'existe
              plus, remplace par l'atelier lui-meme — voir BookPageLuxe.js qui
              redirige desormais cette route directement ici) : retour direct
              au tableau de bord. */}
          <Link to="/dashboard" className="atelier-back-link">← {t('main.header.back')}</Link>
          <h1 className="atelier-title">{book.title || t('main.header.untitledBook')}</h1>
          {/* "Enregistré" A COTE DU TITRE (artefact "Atelier Bookipix",
              2026-10-11 : "rien n'indique aujourd'hui si le travail est
              sauvegarde... 'Enregistré' a cote du titre"). saveStatus
              existait deja (idle/saving/saved/error, voir la sauvegarde
              automatique plus haut) mais n'etait visible que DANS le
              panneau "Mise en page" (saveStatus passe a AtelierLayoutPanel
              plus bas) — invisible des qu'on regardait simplement le livre.
              idle -> rien : une page jamais touchee n'a rien a signaler. */}
          {saveStatus === 'saved' && (
            <span className="atelier-saved-indicator" aria-live="polite">
              <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path d="m3 8.5 3.2 3.2L13 4.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {t('main.header.saved')}
            </span>
          )}
          {saveStatus === 'saving' && (
            <span className="atelier-saved-indicator is-saving" aria-live="polite">{t('main.header.saving')}</span>
          )}
          {saveStatus === 'locked' && (
            <span className="atelier-saved-indicator is-error" aria-live="polite">{t('store.saveState.locked')}</span>
          )}
          {saveStatus === 'offline' && (
            <span className="atelier-saved-indicator is-error" aria-live="polite">{t('store.saveState.offline')}</span>
          )}
          {/* Le FORMAT reste visible pendant toute la composition : il decide de
              la taille reelle des cadres photo — donc de la resolution
              necessaire — et du prix. Le choisir au depart puis l'oublier
              reviendrait a composer sans savoir ce qu'on fabrique
              (decision produit 2026-09-15). Les dimensions viennent de la meme
              table que le rendu. */}
          <span className="atelier-header-note">
            {formatCourant
              ? `${formatCourant.nom} · ${formatCourant.taille}${totalPages ? ` · ${t('main.header.pagesCount', { count: totalPages })}` : ''}`
              : t('main.header.customAtelier')}
            {/* Prix discret (§6 : "STANDARD · 34 pages · 44,30 €" ou
                equivalent — jamais un panneau, juste ce texte). */}
            {Number.isFinite(priceInfo?.bookPriceCents) && ` · ${formatEuros(priceInfo.bookPriceCents, priceInfo.currency)}`}
            {/* Indication breve au moment d'un changement (§7/§8/§11) — puis
                disparait toute seule (showPriceDelta/priceDeltaTimeoutRef). */}
            {priceDelta && (
              <span className="atelier-header-price-delta">
                {priceDelta.pages ? `${priceDelta.pages > 0 ? '+' : ''}${t('main.header.pagesCount', { count: priceDelta.pages })} · ` : ''}
                {formatEurosDelta(priceDelta.priceCents, priceDelta.currency)}
              </span>
            )}
            {/* Le verrouillage apres paiement (mode test) vit desormais dans
                le bandeau detachable ci-dessus (Gabarit 2, retour utilisateur
                2026-09-29) — la ligne de titre ne porte plus que ce qui
                concerne le livre lui-meme (nom, format, prix). */}
          </span>

          {/* "Terminer mon livre" SUR LA LIGNE DU TITRE, tout a droite
              (retour utilisateur, 2026-09-29 : "je le voyais plus haut, en
              face de [titre]... tout a droite" — l'essai precedent le
              placait plus bas, aux cotes d'Inviter/Composer automatiquement,
              ce n'etait pas ce qui etait attendu). margin-left:auto le pousse
              en bout de cette ligne, quel que soit ce que le titre/format
              occupent deja. */}
          {book.page_count ? (
            <button
              type="button"
              className="btn btn-primary atelier-header-finish-btn"
              onClick={handleFinishBook}
              disabled={finishing}
            >
              {t('main.header.finishBook')} →
            </button>
          ) : null}
        </div>

        {/* Ligne 2 : Inviter/Composer automatiquement/? — Photos/Mise en page
            a quitte cette ligne le 2026-10-11 (artefact "Atelier Bookipix") :
            ces deux onglets vivent desormais DANS le panneau fixe de gauche
            (AtelierToolsBar y est toujours utilise, voir plus bas), qui ne
            disparait plus jamais — plus besoin de le centrer contre une
            barre d'onglets qui n'est plus sur cette ligne. */}
        {book.page_count ? (
          <div className="atelier-tools-row">
            <span className="atelier-header-secondary">
              {/* Bouton « Outils » : n'existe que sous 1000 px, ou le panneau devient un tiroir. */}
              <button
                type="button"
                className="atelier-header-link atelier-panel-toggle"
                onClick={() => setPanelDrawerOpen(true)}
                aria-expanded={panelDrawerOpen}
              >
                {t('main.header.panelOpen')}
              </button>
              <button
                type="button"
                className="atelier-header-link atelier-focus-btn"
                onClick={() => setFocusMode((value) => !value)}
                aria-pressed={focusMode}
                title={t('main.header.focusTitle')}
              >
                {focusMode ? t('main.header.focusCollapse') : t('main.header.focusExpand')}
              </button>
              {/* "Inviter" (retour utilisateur, 2026-09-29 : simplification
                  de l'en-tete — un seul libelle constant, plus de "Gerer le
                  collectif" une fois active). Ouvre l'activation si le mode
                  collectif n'est pas encore active, sinon la page de suivi
                  (lien, invitations, contributions) — voir handleInvite. */}
              {/* Livre paye et verrouille : plus d'invitation possible (refonte du collectif, phase 1). */}
              {book?.collection_mode !== 'solo' && !(book?.locked_at && !book?.bookEditBypassed) && (
                <button
                  type="button"
                  className="atelier-header-link"
                  onClick={handleInvite}
                  title={book?.collective_activated_at
                    ? t('main.header.inviteTitleManage')
                    : t('main.header.inviteTitleNew')}
                >
                  {t('main.header.invite')}
                  {jaugeCollectif && jaugeCollectif.total > 0 && (
                    <span className="atelier-invite-badge" aria-label={t('main.header.inviteGauge', { done: jaugeCollectif.ontContribue, total: jaugeCollectif.total })}>
                      {jaugeCollectif.ontContribue}/{jaugeCollectif.total}
                    </span>
                  )}
                </button>
              )}
              <button
                type="button"
                className="atelier-header-link"
                onClick={handleGenerateButtonClick}
                title={t('main.header.composeAutoTitle')}
              >
                ✦ {t('main.header.composeAuto')}
              </button>
              <button
                type="button"
                className="atelier-help-btn"
                onClick={() => setShowOnboarding(true)}
                title={t('main.header.reviewHelp')}
                aria-label={t('main.header.reviewHelp')}
              >
                ?
              </button>
            </span>
          </div>
        ) : null}
      </header>

      <AtelierConfirmSwitchDialog
        isOpen={isConfirmSwitchOpen}
        onCancel={() => setIsConfirmSwitchOpen(false)}
        onConfirm={handleConfirmSwitch}
      />

      <AtelierGenerateModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
        error={generateError}
        estimatedPages={estimatedPages}
        debordement={debordement}
        loadingEstimate={loadingEstimate}
        minPages={MIN_AUTO_PAGES}
        manualPagesCount={manualPagesCount}
      />

      {error && <div className="wizard-error atelier-error">{error}</div>}

      {showOnboarding && (
        <AtelierOnboarding
          onDismiss={() => {
            localStorage.setItem(`${ONBOARDING_SEEN_KEY_PREFIX}${bookId}`, '1');
            setShowOnboarding(false);
          }}
        />
      )}

      {book.page_count ? (
        <>
          {/* UN PANNEAU QUI NE BOUGE PLUS (refonte 2026-10-11, artefact
              "Atelier Bookipix" : "le panneau reste a gauche, la page reste
              au centre, rien ne saute"). Remplace le systeme de TIROIRS du
              2026-09-25/26 (AtelierDrawer, un rail Photos a gauche ET un
              rail Mise en page a droite, ouverts/fermes independamment,
              chacun decalant visuellement le livre a son ouverture) par un
              SEUL panneau fixe a gauche, toujours a la meme largeur : seul
              SON CONTENU change entre les deux onglets (AtelierToolsBar,
              desormais a l'interieur de ce panneau). Le livre ne bouge plus
              jamais, quel que soit l'onglet actif. */}
          <div className={`atelier-workspace ${focusMode ? 'is-focus' : ''}`}>
            {focusMode && (
              <button type="button" className="atelier-focus-exit" onClick={() => setFocusMode(false)}>
                {t('main.header.focusExit')}
              </button>
            )}
            {panelDrawerOpen && <div className="atelier-panel-backdrop" onClick={() => setPanelDrawerOpen(false)} aria-hidden="true" />}
            <aside className={`atelier-panel ${panelDrawerOpen ? 'is-drawer-open' : ''} ${(pickerTargetSlotIndex != null || pickerTargetCoverFace) ? 'is-picking' : ''}`}>
              {/* Champ fichier cache : ouvert directement quand la bibliotheque est vide (voir openFileDialogIfLibraryEmpty). */}
              <input
                ref={bindDirectFileInput}
                type="file"
                accept="image/*"
                hidden
                tabIndex={-1}
                aria-hidden="true"
                onChange={handleDirectFileChosen}
              />
              <button type="button" className="atelier-panel-drawer-close" onClick={() => setPanelDrawerOpen(false)}>
                {t('main.header.panelClose')}
              </button>
              <AtelierToolsBar
                activeDrawer={activeDrawer}
                onToggle={(drawer) => {
                  // Cliquer un onglet rend toujours la main : fin du choix de photo en cours, de la photo selectionnee et de
                  // l edition de texte. Sans cela, cliquer l onglet DEJA actif (changement d etat nul) ne faisait rien et
                  // « Choisissez la photo pour cette page » restait affiche (retour utilisateur 2026-10-17).
                  handleClosePhotoPicker();
                  setSelectedFraming(null);
                  setEditingTextSlot(null);
                  setActiveDrawer(drawer);
                  // Le panneau n'est visible que s'il a une place : en mode Agrandir on en sort, sur un ecran etroit on ouvre
                  // le tiroir. Sans cela, l'onglet « Mise en page » faisait disparaitre la feuille de choix sans rien montrer
                  // a la place (« impossible de retrouver la mise en page », retour 2026-10-21).
                  if (focusMode) setFocusMode(false);
                  else if (window.matchMedia && window.matchMedia('(max-width: 1000px) and (min-width: 761px)').matches) setPanelDrawerOpen(true);
                }}
                photosCount={photos.length}
                layoutLabel={viewKind === 'spread' ? t('toolsBar.layout') : t('bookView.coverLabel')}
              />
              <div className="atelier-panel-body">
                {(pickerTargetSlotIndex != null || pickerTargetCoverFace) ? (
                  <AtelierPhotoPickPanel
                    target={pickerTargetCoverFace
                      ? (pickerTargetCoverFace === 'front' ? t('pickPanel.targetCover') : t('pickPanel.targetBack'))
                      : (() => {
                        const photoSlots = ((draftLayoutForOverlay && draftLayoutForOverlay.slots) || [])
                          .map((type, i) => (type === 'photo' ? i : -1)).filter((i) => i >= 0);
                        return photoSlots.length <= 1
                          ? t('pickPanel.targetPage')
                          : t('pickPanel.targetSlot', { number: photoSlots.indexOf(pickerTargetSlotIndex) + 1 });
                      })()}
                    photos={photos}
                    usedItemIds={usedItemIds}
                    onPick={handlePickPhotoFromLibrary}
                    onUpload={handleUploadPhotoForPicker}
                    uploading={pickerUploading}
                    error={pickerUploadError}
                    onCancel={handleClosePhotoPicker}
                  />
                ) : (selectedSlotIndex != null && selectionItem) || (selectedCoverFace && selectionItem) ? (
                  <AtelierPhotoPanel
                    item={selectionItem}
                    framing={selectionFraming}
                    croppedPct={selectionCropped}
                    isCover={Boolean(selectedCoverFace)}
                    spread={draftLayoutSlug === 'FULL_PHOTO_SPREAD'}
                    caption={selectedSlotItem ? (typeof draftPhotoCaptions[selectedSlotItem.id] === 'string' ? draftPhotoCaptions[selectedSlotItem.id] : draftPhotoCaptions[selectedSlotItem.id]?.texte) : ''}
                    onBack={() => setSelectedFraming(null)}
                    onFit={(fit) => changeSelectionFraming(fit === 'contain' ? { fit, zoom: 1 } : { fit })}
                    onZoom={handlePanelZoom}
                    onReset={() => changeSelectionFraming({ fit: 'cover', focusX: 50, focusY: 50, zoom: 1 })}
                    onCaption={selectedSlotItem ? (text) => handleSaveCaption(selectedSlotItem.id, text, draftPhotoCaptions[selectedSlotItem.id]?.couleur) : null}
                    onReplace={handleReplaceSelection}
                    onRemove={handleRemoveSelection}
                  />
                ) : editingTextSlot != null && viewKind === 'spread' ? (
                  <AtelierTextPanel onBack={() => setEditingTextSlot(null)} />
                ) : activeDrawer === 'photos' ? (
                  <AtelierSidebar
                    estSolo={book?.collection_mode === 'solo'}
                    photos={photos}
                    souvenirs={souvenirs}
                    onItemDragStart={() => {
                      // Ecran etroit : le tiroir ouvert masque la page ou deposer la photo. On le replie des que le glisser
                      // commence (apres le demarrage, sinon le navigateur annule le glisser).
                      if (window.matchMedia && window.matchMedia('(max-width: 1000px) and (min-width: 761px)').matches) {
                        setTimeout(() => setPanelDrawerOpen(false), 0);
                      }
                    }}
                    selectedItem={selectedSidebarItem}
                    onSelectItem={setSelectedSidebarItem}
                    onUploadPhotos={handleUploadPhotos}
                    onDeleteItem={handleDeleteItem}
                    uploadingPhotos={uploadingPhotos}
                    uploadProgress={uploadProgress}
                    onDeleteAll={handleDeleteAll}
                    deletingAll={deletingAll}
                    addError={sidebarAddError}
                    initialTab={searchParams.get('tab')}
                    usedItemIds={usedItemIds}
                    onPlaceText={handlePlaceTextItem}
                  />
                ) : viewKind === 'spread' ? (
                  <AtelierLayoutPanel
                    draftLayoutSlug={draftLayoutSlug}
                    onChooseLayout={handleChooseLayout}
                    pagePhotos={draftSlotItems.filter((entry) => entry && entry.kind === 'photo')}
                    draftPageKind={draftPageKind}
                    draftPageKindFields={draftPageKindFields}
                    onChoosePageKind={handleChoosePageKind}
                    onPageKindFieldChange={handlePageKindFieldChange}
                    photos={photos}
                    printFormat={book.print_format}
                    availableSlugs={availableLayoutSlugs}
                    doublePagePossible={doublePagePossible}
                    // "Vider cette page" et "Position dans le livre" sont passes sur
                    // la page elle-meme (voir pageActions plus bas).
                  />
                ) : (
                  <AtelierCoverPanel
                    book={book}
                    face={viewKind === 'cover' ? 'front' : 'back'}
                    onEditCover={editCover}
                    onSelectFace={(target) => {
                      const current = viewKind === 'cover' ? 'front' : 'back';
                      if (target !== current) goToView(target === 'front' ? 0 : lastViewIndex);
                    }}
                    coverPhotoUrl={coverPhotoItem?.metadata?.thumbnailUrl || coverPhotoItem?.url || null}
                    coverHtml={coverHtml}
                    backCoverHtml={backCoverHtml}
                    libraryPhotos={photos}
                    onChangePhoto={handleOpenCoverPhotoPicker}
                  />
                )}
              </div>
            </aside>

            <div className="atelier-stage-column">
              <AtelierFlipBookView
              spreadPhoto={viewHasSpreadPhoto}
              photoUpgrades={photoUpgrades}
              lowResolution={viewKind === 'spread' && [leftPageIndex, rightPageIndex].some((index) => index != null && qualityWarningsByPage[index] > 0)}
              coverText={{
                values: { title: book.title || '', subtitle: coverOverridesNow.subtitle || '', dateLabel: coverOverridesNow.dateLabel || '' },
                onChange: handleCoverTextChange
              }}
              viewKind={viewKind}
              loading={loadingPreview}
              previewFailed={previewFailed}
              onRetryPreview={() => previewLoader.retryFailed()}
              totalPages={totalPages}
              coverHtml={coverHtml}
              backCoverHtml={backCoverHtml}
              pagePreviewCache={pagePreviewCache}
              leftPageIndex={leftPageIndex}
              rightPageIndex={rightPageIndex}
              currentPageIndex={currentPageIndex}
              selectedSide={selectedSide}
              onSelectSide={setSelectedSide}
              // Feuillet tourne par l'utilisateur (glisser/clic sur le bord
              // du livre, geste natif de page-flip) -> meme fonction que le
              // tiroir "Pages" (AtelierPageFilmstrip) et les sauts directs
              // ci-dessous : goToFilmstripTarget sait deja convertir
              // 'cover'/'back-cover'/un index de page en viewIndex+
              // selectedSide, inutile de dupliquer cette conversion.
              onNavigate={goToFilmstripTarget}
              onPrevious={() => canGoPrevious && goToView(viewIndex - 1)}
              onNext={() => canGoNext && goToView(viewIndex + 1)}
              canGoPrevious={canGoPrevious}
              canGoNext={canGoNext}
              navLabel={navLabel}
              // Sauts directs couverture/4e (retour utilisateur, 2026-09-29 :
              // "les mettre aussi dans l'atelier et sa prévisualisation",
              // apres la meme harmonisation sur Apercu final) — meme
              // fonction que le tiroir "Pages" (AtelierPageFilmstrip),
              // deja disponible ici.
              onGoToCover={() => goToFilmstripTarget('cover')}
              onGoToBackCover={() => goToFilmstripTarget('back-cover')}
              overlay={pageOverlay}
              twinOverlay={twinOverlay}
              twinPageIndex={twinPageIndex}
              pageActions={pageActions}
              printFormat={book.print_format}
              onAssignCoverPhoto={handleAssignCoverPhoto}
              selectedCoverFace={selectedCoverFace}
              onSelectCoverFace={(face) => setSelectedFraming(face ? { type: 'cover', face } : null)}
              coverFraming={selectedCoverFace ? coverFramingOf(selectedCoverFace) : null}
              coverPhotoItem={coverPhotoItem}
              onCoverFramingChange={handleCoverFramingChange}
              coverPicker={pickerTargetCoverFace ? {
                face: pickerTargetCoverFace,
                slot: coverPickerSlot,
                open: true,
                photos: photos,
                uploading: pickerUploading,
                error: pickerUploadError,
                onPick: handlePickPhotoFromLibrary,
                onUpload: handleUploadPhotoForPicker,
                onClose: handleClosePhotoPicker,
                title: pickerTargetCoverFace === 'front' ? t('photoPickerModal.coverTitle') : t('photoPickerModal.backCoverTitle'),
                emptyHint: t('photoPickerModal.coverEmptyHint')
              } : null}
              coverHasPhoto={Boolean(coverPhotoItem)}
              onOpenCoverPhotoPicker={handleOpenCoverPhotoPicker}
              frontHasPhotoSlot={frontHasPhotoSlot}
              backHasPhotoSlot={backHasPhotoSlot}
              selectedSidebarItem={selectedSidebarItem}
              onOpenLayoutDrawer={openLayoutPanel}
              />
            </div>

            {/* Pellicule de pages TOUJOURS VISIBLE, PLEINE LARGEUR (retour
                utilisateur, 2026-10-11 : "la barre de navigation en bas
                (pages) doit prendre toute la largeur de la page, comme dans
                l'artefact") — sur sa PROPRE ligne de grille qui s'etend sous
                le panneau ET la scene (grid-template-areas "panel stage" /
                "dock dock", voir .atelier-workspace), plutot que confinee
                sous la seule colonne du livre comme avant. Composant
                inchange : seul l'endroit ou il est monte change, il garde
                deja tout ce dont cette place a besoin (couverture/4e,
                glisser-deposer pour reordonner, +2/-2 pages, apercu au
                survol). */}
            <div className="atelier-dock">
              <AtelierPageFilmstrip
                pageStatuses={finishStats.pageStatuses}
                qualityWarningsByPage={qualityWarningsByPage}
                limitsByPage={limitsByPage}
                activeTarget={viewKind === 'cover' ? 'cover' : viewKind === 'back-cover' ? 'back-cover' : currentPageIndex}
                printFormat={book.print_format}
                onSelect={goToFilmstripTarget}
                onAddPages={handleAddPages}
                addingPages={addingPages}
                onRemovePages={handleRemovePages}
                removingPages={removingPages}
                canRemovePages={totalPages - 2 >= MIN_AUTO_PAGES}
                minPages={MIN_AUTO_PAGES}
                onMovePage={handleMovePage}
                movingPage={movingPage}
                onRequestPreview={requestFilmstripPreview}
                previewHtml={(target) => previewLoader.get(filmstripKey(target)) ?? null}
                onVisibleTargetsChange={handleFilmstripVisible}
                onInsertPages={handleInsertPages}
                // La suppression de pages vit dans le menu ⋯ de la page courante (correctif 11), plus de « × » dans la barre.
              />
            </div>
          </div>
        </>
      ) : null}

      <AtelierDuplicatePhotosModal
        isOpen={Boolean(doublonsEnAttente)}
        doublons={doublonsEnAttente?.doublons || []}
        nombreNouvelles={doublonsEnAttente?.nouvelles?.length || 0}
        onCancel={() => setDoublonsEnAttente(null)}
        onAddNewOnly={() => {
          const nouvelles = doublonsEnAttente?.nouvelles || [];
          setDoublonsEnAttente(null);
          envoyerLesPhotos(nouvelles);
        }}
        onAddAnyway={() => {
          const selection = doublonsEnAttente?.selection || [];
          setDoublonsEnAttente(null);
          envoyerLesPhotos(selection);
        }}
      />

      {/* Plus d'inventaire (correctif 12) : cette fenetre ne s'ouvre que pour une vraie erreur bloquante. */}
      <AtelierFinishModal
        isOpen={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
        pageIndex={0}
        onGoToPage={goToFilmstripTarget}
      />

      {panneauCollectif === 'invite' && book && (
        <CollectiveInvitePanel
          book={book}
          onClose={() => setPanneauCollectif(null)}
          // On reste dans l'atelier : le livre est mis a jour sur place, puis « Mon collectif » prend le relais.
          onActivated={(livre) => { if (livre) setBook((previous) => ({ ...previous, ...livre })); setPanneauCollectif('manage'); }}
        />
      )}
      {panneauCollectif === 'manage' && book && (
        <CollectiveManagePanel
          book={book}
          usedItemIds={usedItemIds}
          onClose={() => setPanneauCollectif(null)}
          onChanged={() => { resyncItems(); }}
        />
      )}

      {/* Messages flottants, en bas : ne deplacent jamais la mise en page. */}
      <AtelierToasts />
    </div>
  );
}

// Le store (livre + interface) est RECREE pour chaque livre (key) : naviguer
// vers la copie d'un livre ne doit jamais garder l'etat de l'ancien.
export default function BookAtelierLuxe() {
  const { bookId } = useParams();
  return (
    <BookStoreProvider key={bookId}>
      <BookAtelierInner />
    </BookStoreProvider>
  );
}
